import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Award,
  ArrowLeft,
  Users,
  TrendingUp,
  Gift,
  Ticket,
  Plus,
  Pencil,
  Trash2,
  ToggleLeft,
  ToggleRight,
  ChevronRight,
  ChevronDown,
  Star,
  Settings,
  Activity,
  Trophy,
  Zap,
  Tag,
  Calendar,
  Target,
  Flame,
  Crosshair,
  Play,
  Pause as PauseIcon,
  Check,
  Package,
  Gamepad2,
  Wand2,
  type LucideIcon,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input, Select, Textarea } from '../components/ui/Input.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { Pagination } from '../components/ui/Pagination.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface LoyaltyProgram {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  settings: { description?: string; pointsPerUnit?: number; currency?: string } | null;
  createdAt: string;
  updatedAt: string;
}
interface ProgramStats {
  totalEnrolled: number;
  totalPointsIssued: number;
  totalPointsRedeemed: number;
  activeCoupons: number;
}
interface Tier {
  id: string;
  programId: string;
  code: string;
  name: string;
  minPoints: number;
  color: string;
  rank: number;
}
interface Reward {
  id: string;
  programId: string;
  name: string;
  description: string | null;
  type: string;
  pointsCost: number;
  value: { amount: number; unit: 'percent' | 'fixed' | 'free' };
  availableQuantity: number | null;
  redeemedCount: number;
  isActive: boolean;
  imageUrl: string | null;
  createdAt: string;
}
interface Member {
  accountId: string;
  customerId: string;
  pointsBalance: number;
  tierCode: string | null;
  enrolledAt: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}
interface LedgerEntry {
  id: string;
  customerId: string;
  type: string;
  points: number;
  balanceAfter: number;
  referenceType: string | null;
  description: string | null;
  occurredAt: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}
interface CouponRow {
  id: string;
  customerId: string;
  code: string;
  type: string;
  value: { amount: number; unit: string };
  status: string;
  issuedAt: string;
  expiresAt: string | null;
  usedAt: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string;
}
interface PagedResult<T> {
  items: T[];
  total: number;
}

// ── Editorial types ────────────────────────────────────────────────────────────
interface PointRule {
  id: string;
  programId: string;
  ruleType: string;
  points: number;
  dailyCap: number | null;
  streakThreshold: number | null;
  minTimeOnPageSeconds: number | null;
  contributesToLeaderboard: boolean;
  isActive: boolean;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}
interface BadgeDefinition {
  id: string;
  programId: string;
  code: string;
  name: string;
  description: string | null;
  iconUrl: string | null;
  conditionType: string;
  conditionThreshold: number;
  benefitType: string;
  benefitValue: string | null;
  benefitDurationDays: number | null;
  displayOrder: number;
  isActive: boolean;
}
interface InterestCategory {
  id: string;
  code: string;
  name: string;
  description: string | null;
  iconUrl: string | null;
  displayOrder: number;
  isActive: boolean;
  topics: InterestTopic[];
}
interface InterestTopic {
  id: string;
  categoryId: string;
  code: string;
  name: string;
  iconUrl: string | null;
  displayOrder: number;
  isActive: boolean;
}
interface Contest {
  id: string;
  programId: string;
  title: string;
  description: string | null;
  contestType: 'instant_win' | 'draw' | 'leaderboard';
  status: 'draft' | 'active' | 'paused' | 'completed' | 'cancelled';
  startDate: string;
  endDate: string;
  entryConfig: Record<string, unknown>;
  config: Record<string, unknown>;
  winValidation: 'auto' | 'manual';
  createdAt: string;
}
interface ContestRound {
  id: string;
  contestId: string;
  name: string;
  startAt: string;
  endAt: string;
  status: 'upcoming' | 'active' | 'completed';
  createdAt: string;
}
interface ContestPrize {
  id: string;
  roundId: string;
  contestId: string;
  name: string;
  prizeType: 'points' | 'coupon' | 'physical' | 'voucher' | 'custom';
  prizeValue: Record<string, unknown>;
  status: 'available' | 'won' | 'expired' | 'recovered';
  isRecovered: boolean;
  winnerId: string | null;
  validationStatus: 'pending' | 'confirmed' | 'rejected' | null;
  wonAt: string | null;
}
interface LeaderboardEntry {
  customerId: string;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  score: number;
  rank: number;
  previousRank: number | null;
  deltaRank: number | null;
}
interface Mission {
  id: string;
  programId: string;
  title: string;
  description: string | null;
  iconUrl: string | null;
  actionType: string;
  actionConfig: Record<string, unknown>;
  requiredCount: number;
  pointsReward: number;
  recurrence: string;
  dayOfWeek: number | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}
interface FlashRewardItem {
  id: string;
  programId: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  rewardType: string;
  rewardValue: Record<string, unknown>;
  startsAt: string;
  endsAt: string;
  maxClaims: number | null;
  claimedCount: number;
  minTierCode: string | null;
  minPoints: number | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(d: string) {
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
function fmtFull(d: string) {
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
function initials(first: string | null, last: string | null, email: string) {
  if (first || last) return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
  return email[0]?.toUpperCase() ?? '?';
}
function fullName(first: string | null, last: string | null, email: string) {
  if (first || last) return `${first ?? ''} ${last ?? ''}`.trim();
  return email;
}

const LEDGER_TYPE_LABELS: Record<string, string> = {
  earned: 'Earned',
  bonus: 'Bonus',
  adjusted: 'Adjusted',
  redeemed: 'Redeemed',
  expired: 'Expired',
};
const LEDGER_TYPE_COLORS: Record<string, string> = {
  earned: 'bg-emerald-100 text-emerald-700',
  bonus: 'bg-sky-100 text-sky-700',
  adjusted: 'bg-amber-100 text-amber-700',
  redeemed: 'bg-rose-100 text-rose-700',
  expired: 'bg-zinc-100 text-zinc-500',
};
const COUPON_STATUS_LABELS: Record<string, string> = {
  issued: 'Issued',
  used: 'Used',
  expired: 'Expired',
  revoked: 'Revoked',
};
const COUPON_STATUS_COLORS: Record<string, string> = {
  issued: 'bg-emerald-100 text-emerald-700',
  used: 'bg-zinc-100 text-zinc-500',
  expired: 'bg-amber-100 text-amber-700',
  revoked: 'bg-red-100 text-red-600',
};

const DEFAULT_TIER_COLORS = ['#71717a', '#94a3b8', '#f59e0b', '#a855f7', '#06b6d4'];

// ─── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  icon: Icon,
  color = 'text-zinc-900',
}: {
  label: string;
  value: string | number;
  icon: LucideIcon;
  color?: string;
}) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-medium text-zinc-400 uppercase tracking-wider">{label}</p>
        <Icon size={15} className="text-zinc-300" />
      </div>
      <p className={`text-2xl font-bold tabular-nums ${color}`}>{value.toLocaleString('en')}</p>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function LoyaltyDetailPage() {
  const { id, section = 'overview' } = useParams<{ id: string; section: string }>();
  const tab = section;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  // Modals
  const [tierModal, setTierModal] = useState<{ open: boolean; tier?: Tier }>({ open: false });
  const [rewardModal, setRewardModal] = useState<{ open: boolean; reward?: Reward }>({
    open: false,
  });
  const [assignPointsModal, setAssignPointsModal] = useState<{ open: boolean; member?: Member }>({
    open: false,
  });
  const [changeTierModal, setChangeTierModal] = useState<{ open: boolean; member?: Member }>({
    open: false,
  });
  const [issueCouponModal, setIssueCouponModal] = useState<{ open: boolean; member?: Member }>({
    open: false,
  });
  const [redeemModal, setRedeemModal] = useState<{ open: boolean; member?: Member }>({
    open: false,
  });
  const [enrollModal, setEnrollModal] = useState(false);
  const [deleteRewardTarget, setDeleteRewardTarget] = useState<Reward | null>(null);
  const [deleteTierTarget, setDeleteTierTarget] = useState<Tier | null>(null);
  const [deactivateConfirm, setDeactivateConfirm] = useState(false);
  const [unenrollTarget, setUnenrollTarget] = useState<Member | null>(null);
  const [deleteProgramConfirm, setDeleteProgramConfirm] = useState(false);

  // Pagination
  const [membersPage, setMembersPage] = useState(1);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [couponsPage, setCouponsPage] = useState(1);
  const [couponStatusFilter, setCouponStatusFilter] = useState('');

  // Data
  const { data: program, isLoading: programLoading } = useQuery<LoyaltyProgram>({
    queryKey: ['loyalty-program', id],
    queryFn: () => api.get(`/api/v1/loyalty/programs/${id}`).then((r) => r.data),
    enabled: !!id,
  });
  const { data: stats } = useQuery<ProgramStats>({
    queryKey: ['loyalty-stats', id],
    queryFn: () => api.get(`/api/v1/loyalty/programs/${id}/stats`).then((r) => r.data),
    enabled: !!id,
  });
  const { data: tiers = [], isLoading: tiersLoading } = useQuery<Tier[]>({
    queryKey: ['loyalty-tiers', id],
    queryFn: () => api.get(`/api/v1/loyalty/programs/${id}/tiers`).then((r) => r.data),
    enabled: !!id,
  });
  const { data: rewards = [], isLoading: rewardsLoading } = useQuery<Reward[]>({
    queryKey: ['loyalty-rewards', id],
    queryFn: () => api.get(`/api/v1/loyalty/programs/${id}/rewards`).then((r) => r.data),
    enabled: !!id && tab === 'rewards',
  });
  const { data: membersData, isLoading: membersLoading } = useQuery<PagedResult<Member>>({
    queryKey: ['loyalty-members', id, membersPage],
    queryFn: () =>
      api
        .get(`/api/v1/loyalty/programs/${id}/members?page=${membersPage}&limit=20`)
        .then((r) => r.data),
    enabled: !!id && (tab === 'members' || tab === 'overview'),
  });
  const { data: ledgerData, isLoading: ledgerLoading } = useQuery<PagedResult<LedgerEntry>>({
    queryKey: ['loyalty-ledger', id, ledgerPage],
    queryFn: () =>
      api
        .get(`/api/v1/loyalty/programs/${id}/ledger?page=${ledgerPage}&limit=30`)
        .then((r) => r.data),
    enabled: !!id && tab === 'ledger',
  });
  const { data: couponsData, isLoading: couponsLoading } = useQuery<PagedResult<CouponRow>>({
    queryKey: ['loyalty-coupons', id, couponsPage, couponStatusFilter],
    queryFn: () =>
      api
        .get(
          `/api/v1/loyalty/programs/${id}/coupons?page=${couponsPage}&limit=30${couponStatusFilter ? `&status=${couponStatusFilter}` : ''}`,
        )
        .then((r) => r.data),
    enabled: !!id && tab === 'coupons',
  });

  // Mutations
  const updateProgram = useMutation({
    mutationFn: (data: Partial<LoyaltyProgram['settings'] & { name: string; isActive: boolean }>) =>
      api.patch(`/api/v1/loyalty/programs/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-program', id] });
      toast.success('Program updated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const deleteTierMutation = useMutation({
    mutationFn: (tierId: string) => api.delete(`/api/v1/loyalty/programs/${id}/tiers/${tierId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-tiers', id] });
      setDeleteTierTarget(null);
      toast.info('Tier removed');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const toggleRewardMutation = useMutation({
    mutationFn: ({ rewardId, isActive }: { rewardId: string; isActive: boolean }) =>
      api.patch(`/api/v1/loyalty/programs/${id}/rewards/${rewardId}`, { isActive }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-rewards', id] });
      toast.success('Reward updated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const deleteRewardMutation = useMutation({
    mutationFn: (rewardId: string) =>
      api.patch(`/api/v1/loyalty/programs/${id}/rewards/${rewardId}`, { isActive: false }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-rewards', id] });
      setDeleteRewardTarget(null);
      toast.info('Reward deactivated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const unenrollMutation = useMutation({
    mutationFn: (customerId: string) =>
      api.delete(`/api/v1/loyalty/programs/${id}/members/${customerId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-members', id] });
      qc.invalidateQueries({ queryKey: ['loyalty-stats', id] });
      setUnenrollTarget(null);
      toast.info('Member unenrolled from program');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const deleteProgramMutation = useMutation({
    mutationFn: () => api.delete(`/api/v1/loyalty/programs/${id}`),
    onSuccess: () => {
      navigate('/loyalty');
      toast.info('Program deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  if (programLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-zinc-100 rounded w-64" />
        <div className="h-4 bg-zinc-100 rounded w-96" />
      </div>
    );
  }
  if (!program) {
    return <EmptyState icon={<Award size={26} />} title="Program not found" description="" />;
  }

  const settings = program.settings ?? {};

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/loyalty')}
          className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-zinc-900 truncate">{program.name}</h1>
            <Badge variant={program.isActive ? 'success' : 'default'} dot>
              {program.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          <p className="text-xs text-zinc-400 mt-0.5">
            <code className="font-mono">{program.slug}</code>
            {settings.pointsPerUnit != null && (
              <>
                {' '}
                · {settings.pointsPerUnit} pt per unit · {settings.currency ?? 'EUR'}
              </>
            )}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex gap-6">
        <div className="flex-1 min-w-0">

      {/* ── Overview ─────────────────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Total enrolled"
              value={stats?.totalEnrolled ?? '—'}
              icon={Users}
              color="text-zinc-900"
            />
            <KpiCard
              label="Points issued"
              value={stats?.totalPointsIssued ?? '—'}
              icon={TrendingUp}
              color="text-emerald-700"
            />
            <KpiCard
              label="Points redeemed"
              value={stats?.totalPointsRedeemed ?? '—'}
              icon={Gift}
              color="text-rose-600"
            />
            <KpiCard
              label="Active coupons"
              value={stats?.activeCoupons ?? '—'}
              icon={Ticket}
              color="text-sky-700"
            />
          </div>

          {/* Recent members */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-zinc-700">Recent members</h3>
              <button
                onClick={() => navigate(`/loyalty/${id}/members`)}
                className="text-xs text-zinc-400 hover:text-zinc-700 flex items-center gap-1"
              >
                View all <ChevronRight size={11} />
              </button>
            </div>
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-100">
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                      Points
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                      Tier
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                      Enrolled
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-50">
                  {membersLoading ? (
                    <SkeletonRows count={5} cols={4} />
                  ) : (membersData?.items ?? []).slice(0, 5).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-sm text-zinc-400">
                        No members yet
                      </td>
                    </tr>
                  ) : (
                    (membersData?.items ?? []).slice(0, 5).map((m) => (
                      <tr key={m.accountId} className="hover:bg-zinc-50/60 transition-colors">
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2.5">
                            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-zinc-100 text-zinc-600 text-xs font-medium flex-shrink-0">
                              {initials(m.firstName, m.lastName, m.email)}
                            </span>
                            <div>
                              <p className="font-medium text-zinc-900 text-xs">
                                {fullName(m.firstName, m.lastName, m.email)}
                              </p>
                              <p className="text-[10px] text-zinc-400">{m.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 font-semibold text-zinc-800 tabular-nums text-xs">
                          {m.pointsBalance.toLocaleString('en')}
                        </td>
                        <td className="px-4 py-2.5">
                          <TierBadge code={m.tierCode ?? 'standard'} tiers={tiers} />
                        </td>
                        <td className="px-4 py-2.5 text-xs text-zinc-500">{fmt(m.enrolledAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Tiers ──────────────────────────────────────────────────────────── */}
      {tab === 'tiers' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-zinc-500">
              Configure program tiers. Thresholds are expressed in points.
            </p>
            <Button size="sm" onClick={() => setTierModal({ open: true })}>
              <Plus size={13} className="mr-1.5" /> Add tier
            </Button>
          </div>
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100">
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Tier
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Min points
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-4 py-3 w-24" />
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-50">
                {tiersLoading ? (
                  <SkeletonRows count={3} cols={5} />
                ) : tiers.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <EmptyState
                        icon={<Star size={24} />}
                        title="No tiers configured"
                        description="Add tiers to your program (e.g. Standard, Silver, Gold)."
                        action={{
                          label: '+ Add tier',
                          onClick: () => setTierModal({ open: true }),
                        }}
                      />
                    </td>
                  </tr>
                ) : (
                  tiers.map((t) => (
                    <tr key={t.id} className="group hover:bg-zinc-50/60 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: t.color }}
                          />
                          <span className="font-medium text-zinc-900">{t.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-xs bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded">
                          {t.code}
                        </code>
                      </td>
                      <td className="px-4 py-3 tabular-nums font-medium text-zinc-700">
                        {t.minPoints.toLocaleString('en')} pt
                      </td>
                      <td className="px-4 py-3 text-zinc-500">{t.rank}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => setTierModal({ open: true, tier: t })}
                            className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                          >
                            <Pencil size={11} />
                          </button>
                          <button
                            onClick={() => setDeleteTierTarget(t)}
                            className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Rewards ────────────────────────────────────────────────────────── */}
      {tab === 'rewards' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-zinc-500">Reward catalog redeemable with points.</p>
            <Button size="sm" onClick={() => setRewardModal({ open: true })}>
              <Plus size={13} className="mr-1.5" /> New reward
            </Button>
          </div>
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100">
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Points cost
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Availability
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 w-28" />
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-50">
                {rewardsLoading ? (
                  <SkeletonRows count={3} cols={6} />
                ) : rewards.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <EmptyState
                        icon={<Gift size={24} />}
                        title="No rewards in catalog"
                        description="Add rewards that customers can redeem with their points."
                        action={{
                          label: '+ New reward',
                          onClick: () => setRewardModal({ open: true }),
                        }}
                      />
                    </td>
                  </tr>
                ) : (
                  rewards.map((r) => {
                    const soldOut =
                      r.availableQuantity !== null && r.redeemedCount >= r.availableQuantity;
                    return (
                      <tr key={r.id} className="group hover:bg-zinc-50/60 transition-colors">
                        <td className="px-4 py-3">
                          <p className="font-medium text-zinc-900">{r.name}</p>
                          {r.description && (
                            <p className="text-xs text-zinc-400 mt-0.5 truncate max-w-[200px]">
                              {r.description}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="info">{r.type}</Badge>
                        </td>
                        <td className="px-4 py-3 font-semibold text-zinc-800 tabular-nums">
                          {r.pointsCost.toLocaleString('en')} pt
                        </td>
                        <td className="px-4 py-3 text-xs text-zinc-500">
                          {r.availableQuantity === null ? (
                            <span className="text-zinc-400">Unlimited</span>
                          ) : soldOut ? (
                            <Badge variant="danger">Sold out</Badge>
                          ) : (
                            <span>
                              {r.redeemedCount}/{r.availableQuantity}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={r.isActive ? 'success' : 'default'}>
                            {r.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => setRewardModal({ open: true, reward: r })}
                              className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                            >
                              <Pencil size={11} />
                            </button>
                            <button
                              onClick={() =>
                                toggleRewardMutation.mutate({
                                  rewardId: r.id,
                                  isActive: !r.isActive,
                                })
                              }
                              className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                              title={r.isActive ? 'Deactivate' : 'Reactivate'}
                            >
                              {r.isActive ? (
                                <ToggleRight size={13} className="text-emerald-500" />
                              ) : (
                                <ToggleLeft size={13} />
                              )}
                            </button>
                            <button
                              onClick={() => setDeleteRewardTarget(r)}
                              className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                            >
                              <Trash2 size={11} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Members ────────────────────────────────────────────────────────── */}
      {tab === 'members' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-zinc-500">
              {membersData?.total ?? 0} members enrolled in this program.
            </p>
            <Button size="sm" variant="secondary" onClick={() => setEnrollModal(true)}>
              <Plus size={13} className="mr-1.5" /> Enroll member
            </Button>
          </div>
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100">
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Points
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Tier
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Enrolled
                  </th>
                  <th className="px-4 py-3 w-48" />
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-50">
                {membersLoading ? (
                  <SkeletonRows count={8} cols={5} />
                ) : (membersData?.items ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <EmptyState
                        icon={<Users size={24} />}
                        title="No members"
                        description="Enroll customers to start tracking their points."
                        action={{ label: '+ Enroll member', onClick: () => setEnrollModal(true) }}
                      />
                    </td>
                  </tr>
                ) : (
                  (membersData?.items ?? []).map((m) => (
                    <tr key={m.accountId} className="group hover:bg-zinc-50/60 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-zinc-100 text-zinc-600 text-xs font-medium flex-shrink-0">
                            {initials(m.firstName, m.lastName, m.email)}
                          </span>
                          <div>
                            <p className="font-medium text-zinc-900 text-sm">
                              {fullName(m.firstName, m.lastName, m.email)}
                            </p>
                            <p className="text-xs text-zinc-400">{m.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-bold text-zinc-800 tabular-nums">
                        {m.pointsBalance.toLocaleString('en')}
                      </td>
                      <td className="px-4 py-3">
                        <TierBadge code={m.tierCode ?? 'standard'} tiers={tiers} />
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-500">{fmt(m.enrolledAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => setAssignPointsModal({ open: true, member: m })}
                            className="px-2 py-1 text-xs font-medium text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded-md transition-colors"
                          >
                            + Points
                          </button>
                          <button
                            onClick={() => setChangeTierModal({ open: true, member: m })}
                            className="px-2 py-1 text-xs font-medium text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded-md transition-colors"
                          >
                            Tier
                          </button>
                          <button
                            onClick={() => setIssueCouponModal({ open: true, member: m })}
                            className="px-2 py-1 text-xs font-medium text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded-md transition-colors"
                          >
                            Coupon
                          </button>
                          <button
                            onClick={() => setRedeemModal({ open: true, member: m })}
                            className="px-2 py-1 text-xs font-medium text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded-md transition-colors"
                          >
                            Redeem
                          </button>
                          <button
                            onClick={() => setUnenrollTarget(m)}
                            className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            title="Unenroll"
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {membersData && membersData.total > 20 && (
            <div className="mt-4">
              <Pagination
                page={membersPage}
                totalPages={Math.ceil(membersData.total / 20)}
                onPageChange={setMembersPage}
              />
            </div>
          )}
        </div>
      )}

      {/* ── Ledger ─────────────────────────────────────────────────────────── */}
      {tab === 'ledger' && (
        <div>
          <p className="text-sm text-zinc-500 mb-4">
            All point transactions for this program, ordered by date descending.
          </p>
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100">
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Points
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Balance after
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Note
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-50">
                {ledgerLoading ? (
                  <SkeletonRows count={10} cols={6} />
                ) : (ledgerData?.items ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-zinc-400">
                      No transactions yet
                    </td>
                  </tr>
                ) : (
                  (ledgerData?.items ?? []).map((e) => (
                    <tr key={e.id} className="hover:bg-zinc-50/40">
                      <td className="px-4 py-2.5 text-xs text-zinc-500 whitespace-nowrap">
                        {fmtFull(e.occurredAt)}
                      </td>
                      <td className="px-4 py-2.5">
                        <p className="text-xs font-medium text-zinc-800">
                          {fullName(e.firstName, e.lastName, e.email)}
                        </p>
                        <p className="text-[10px] text-zinc-400">{e.email}</p>
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${LEDGER_TYPE_COLORS[e.type] ?? 'bg-zinc-100 text-zinc-500'}`}
                        >
                          {LEDGER_TYPE_LABELS[e.type] ?? e.type}
                        </span>
                      </td>
                      <td
                        className={`px-4 py-2.5 font-bold tabular-nums text-sm ${e.points > 0 ? 'text-emerald-600' : 'text-rose-600'}`}
                      >
                        {e.points > 0 ? '+' : ''}
                        {e.points.toLocaleString('en')}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-xs text-zinc-600">
                        {e.balanceAfter.toLocaleString('en')}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-zinc-400 max-w-[180px] truncate">
                        {e.description ?? e.referenceType ?? '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {ledgerData && ledgerData.total > 30 && (
            <div className="mt-4">
              <Pagination
                page={ledgerPage}
                totalPages={Math.ceil(ledgerData.total / 30)}
                onPageChange={setLedgerPage}
              />
            </div>
          )}
        </div>
      )}

      {/* ── Coupons ────────────────────────────────────────────────────────── */}
      {tab === 'coupons' && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <p className="text-sm text-zinc-500 flex-1">All coupons issued in this program.</p>
            <Select
              value={couponStatusFilter}
              onChange={(e) => {
                setCouponStatusFilter(e.target.value);
                setCouponsPage(1);
              }}
              className="w-36"
            >
              <option value="">All statuses</option>
              <option value="issued">Issued</option>
              <option value="used">Used</option>
              <option value="expired">Expired</option>
              <option value="revoked">Revoked</option>
            </Select>
          </div>
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100">
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Type / Value
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Issued
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Expires
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-50">
                {couponsLoading ? (
                  <SkeletonRows count={8} cols={6} />
                ) : (couponsData?.items ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-zinc-400">
                      No coupons found
                    </td>
                  </tr>
                ) : (
                  (couponsData?.items ?? []).map((c) => (
                    <tr key={c.id} className="hover:bg-zinc-50/40">
                      <td className="px-4 py-2.5">
                        <code className="text-xs bg-zinc-100 text-zinc-700 px-1.5 py-0.5 rounded font-mono tracking-wider">
                          {c.code}
                        </code>
                      </td>
                      <td className="px-4 py-2.5">
                        <p className="text-xs font-medium text-zinc-800">
                          {fullName(c.firstName, c.lastName, c.email)}
                        </p>
                        <p className="text-[10px] text-zinc-400">{c.email}</p>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-zinc-600">
                        <span className="font-medium">{c.type}</span>
                        {c.value?.amount != null && (
                          <span className="text-zinc-400">
                            {' '}
                            · {c.value.amount}
                            {c.value.unit === 'percent'
                              ? '%'
                              : c.value.unit === 'fixed'
                                ? ' €'
                                : ''}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${COUPON_STATUS_COLORS[c.status] ?? 'bg-zinc-100 text-zinc-500'}`}
                        >
                          {COUPON_STATUS_LABELS[c.status] ?? c.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-zinc-500">{fmt(c.issuedAt)}</td>
                      <td className="px-4 py-2.5 text-xs text-zinc-500">
                        {c.expiresAt ? fmt(c.expiresAt) : <span className="text-zinc-300">—</span>}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {couponsData && couponsData.total > 30 && (
            <div className="mt-4">
              <Pagination
                page={couponsPage}
                totalPages={Math.ceil(couponsData.total / 30)}
                onPageChange={setCouponsPage}
              />
            </div>
          )}
        </div>
      )}

      {/* ── Point Rules ─────────────────────────────────────────────────────── */}
      {tab === 'point-rules' && <PointRulesTab programId={id!} />}

      {/* ── Badges ─────────────────────────────────────────────────────────── */}
      {tab === 'badges' && <BadgesTab programId={id!} />}

      {/* ── Interests ──────────────────────────────────────────────────────── */}
      {tab === 'interests' && <InterestsTab />}

      {/* ── Contests ───────────────────────────────────────────────────────── */}
      {tab === 'contests' && <ContestsTab programId={id!} />}

      {/* ── Missions ─────────────────────────────────────────────────────── */}
      {tab === 'missions' && <MissionsTab programId={id!} />}

      {/* ── Flash Rewards ────────────────────────────────────────────────── */}
      {tab === 'flash-rewards' && <FlashRewardsTab programId={id!} />}

      {/* ── Leaderboard ────────────────────────────────────────────────────── */}
      {tab === 'leaderboard' && <LeaderboardTab programId={id!} />}

      {/* ── Settings ───────────────────────────────────────────────────────── */}
      {tab === 'settings' && (
        <SettingsTab
          program={program}
          onUpdate={(data) => updateProgram.mutate(data)}
          loading={updateProgram.isPending}
          onDeactivate={() => setDeactivateConfirm(true)}
          onDeleteProgram={() => setDeleteProgramConfirm(true)}
        />
      )}

        </div>{/* end flex-1 content */}
      </div>{/* end sidebar+content flex */}

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      <TierModal
        key={tierModal.tier?.id ?? 'new-tier'}
        open={tierModal.open}
        tier={tierModal.tier}
        programId={id!}
        tiersCount={tiers.length}
        onClose={() => setTierModal({ open: false })}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['loyalty-tiers', id] });
          setTierModal({ open: false });
        }}
      />
      <RewardModal
        key={rewardModal.reward?.id ?? 'new-reward'}
        open={rewardModal.open}
        reward={rewardModal.reward}
        programId={id!}
        onClose={() => setRewardModal({ open: false })}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['loyalty-rewards', id] });
          setRewardModal({ open: false });
        }}
      />
      <AssignPointsModal
        open={assignPointsModal.open}
        member={assignPointsModal.member}
        programId={id!}
        onClose={() => setAssignPointsModal({ open: false })}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['loyalty-members', id] });
          qc.invalidateQueries({ queryKey: ['loyalty-stats', id] });
          qc.invalidateQueries({ queryKey: ['loyalty-ledger', id] });
          setAssignPointsModal({ open: false });
        }}
      />
      <ChangeTierModal
        open={changeTierModal.open}
        member={changeTierModal.member}
        programId={id!}
        tiers={tiers}
        onClose={() => setChangeTierModal({ open: false })}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['loyalty-members', id] });
          setChangeTierModal({ open: false });
        }}
      />
      <IssueCouponModal
        open={issueCouponModal.open}
        member={issueCouponModal.member}
        onClose={() => setIssueCouponModal({ open: false })}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['loyalty-coupons', id] });
          qc.invalidateQueries({ queryKey: ['loyalty-stats', id] });
          setIssueCouponModal({ open: false });
        }}
      />
      <RedeemRewardModal
        open={redeemModal.open}
        member={redeemModal.member}
        programId={id!}
        rewards={rewards}
        onClose={() => setRedeemModal({ open: false })}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['loyalty-members', id] });
          qc.invalidateQueries({ queryKey: ['loyalty-stats', id] });
          qc.invalidateQueries({ queryKey: ['loyalty-ledger', id] });
          setRedeemModal({ open: false });
        }}
      />
      <EnrollCustomerModal
        open={enrollModal}
        programId={id!}
        onClose={() => setEnrollModal(false)}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['loyalty-members', id] });
          qc.invalidateQueries({ queryKey: ['loyalty-stats', id] });
          setEnrollModal(false);
        }}
      />

      <ConfirmDialog
        open={!!deleteRewardTarget}
        title="Deactivate reward?"
        message={`"${deleteRewardTarget?.name}" will be deactivated and can no longer be redeemed.`}
        confirmLabel="Deactivate"
        variant="warning"
        loading={deleteRewardMutation.isPending}
        onConfirm={() => deleteRewardTarget && deleteRewardMutation.mutate(deleteRewardTarget.id)}
        onCancel={() => setDeleteRewardTarget(null)}
      />
      <ConfirmDialog
        open={!!deleteTierTarget}
        title="Delete tier?"
        message={`Tier "${deleteTierTarget?.name}" will be removed. Customers with this tier will keep the tier code but it will no longer be configured.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteTierMutation.isPending}
        onConfirm={() => deleteTierTarget && deleteTierMutation.mutate(deleteTierTarget.id)}
        onCancel={() => setDeleteTierTarget(null)}
      />
      <ConfirmDialog
        open={deactivateConfirm}
        title={program.isActive ? 'Deactivate program?' : 'Reactivate program?'}
        message={
          program.isActive
            ? 'Customers will no longer be able to earn points or redeem rewards. Existing enrollments will be preserved.'
            : 'The program will become active again and customers will be able to earn points.'
        }
        confirmLabel={program.isActive ? 'Deactivate' : 'Reactivate'}
        variant="warning"
        loading={updateProgram.isPending}
        onConfirm={() => {
          updateProgram.mutate({ isActive: !program.isActive } as never);
          setDeactivateConfirm(false);
        }}
        onCancel={() => setDeactivateConfirm(false)}
      />
      <ConfirmDialog
        open={!!unenrollTarget}
        title="Unenroll member?"
        message={`"${unenrollTarget ? fullName(unenrollTarget.firstName, unenrollTarget.lastName, unenrollTarget.email) : ''}" will be removed from the program and will lose all accumulated points.`}
        confirmLabel="Unenroll"
        variant="danger"
        loading={unenrollMutation.isPending}
        onConfirm={() => unenrollTarget && unenrollMutation.mutate(unenrollTarget.customerId)}
        onCancel={() => setUnenrollTarget(null)}
      />
      <ConfirmDialog
        open={deleteProgramConfirm}
        title="Delete program?"
        message="The program will be permanently deleted. This action is irreversible and not allowed if there are enrolled members."
        confirmLabel="Delete program"
        variant="danger"
        loading={deleteProgramMutation.isPending}
        onConfirm={() => deleteProgramMutation.mutate()}
        onCancel={() => setDeleteProgramConfirm(false)}
      />
    </div>
  );
}

// ─── TierBadge ────────────────────────────────────────────────────────────────

function TierBadge({ code, tiers }: { code: string; tiers: Tier[] }) {
  const tier = tiers.find((t) => t.code === code);
  const color = tier?.color ?? '#71717a';
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full"
      style={{ backgroundColor: `${color}20`, color }}
    >
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
      {tier?.name ?? code}
    </span>
  );
}

// ─── SettingsTab ──────────────────────────────────────────────────────────────

function SettingsTab({
  program,
  onUpdate,
  loading,
  onDeactivate,
  onDeleteProgram,
}: {
  program: LoyaltyProgram;
  onUpdate: (data: Record<string, unknown>) => void;
  loading: boolean;
  onDeactivate: () => void;
  onDeleteProgram: () => void;
}) {
  const settings = program.settings ?? {};
  const [name, setName] = useState(program.name);
  const [description, setDescription] = useState(settings.description ?? '');
  const [pointsPerUnit, setPointsPerUnit] = useState(String(settings.pointsPerUnit ?? 1));
  const [currency, setCurrency] = useState(settings.currency ?? 'EUR');

  useEffect(() => {
    const s = program.settings ?? {};
    setName(program.name);
    setDescription(s.description ?? '');
    setPointsPerUnit(String(s.pointsPerUnit ?? 1));
    setCurrency(s.currency ?? 'EUR');
  }, [program]);

  return (
    <div className="max-w-xl space-y-6">
      <div className="space-y-4">
        <Input label="Program name" value={name} onChange={(e) => setName(e.target.value)} />
        <Textarea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Points per unit"
            type="number"
            min="0"
            step="0.01"
            value={pointsPerUnit}
            onChange={(e) => setPointsPerUnit(e.target.value)}
          />
          <Input
            label="Currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            placeholder="EUR"
          />
        </div>
        <Input label="Slug" value={program.slug} disabled className="opacity-60" />
      </div>
      <Button
        loading={loading}
        onClick={() =>
          onUpdate({
            name,
            description: description || undefined,
            pointsPerUnit: Number(pointsPerUnit),
            currency,
          })
        }
      >
        Save changes
      </Button>

      <div className="pt-4 border-t border-zinc-100">
        <h3 className="text-sm font-semibold text-zinc-700 mb-1">Program status</h3>
        <p className="text-xs text-zinc-500 mb-3">
          {program.isActive
            ? 'Program is active. Customers can earn points and redeem rewards.'
            : 'Program is inactive. No new point transactions will be processed.'}
        </p>
        <Button
          variant={program.isActive ? 'danger' : 'secondary'}
          size="sm"
          onClick={onDeactivate}
        >
          {program.isActive ? (
            <>
              <ToggleLeft size={13} className="mr-1.5" /> Deactivate program
            </>
          ) : (
            <>
              <ToggleRight size={13} className="mr-1.5" /> Reactivate program
            </>
          )}
        </Button>
      </div>

      <div className="pt-4 border-t border-red-100">
        <h3 className="text-sm font-semibold text-red-600 mb-1">Danger zone</h3>
        <p className="text-xs text-zinc-500 mb-3">
          Deletion is permanent and not allowed if there are enrolled members.
        </p>
        <Button variant="danger" size="sm" onClick={onDeleteProgram}>
          <Trash2 size={13} className="mr-1.5" /> Delete program
        </Button>
      </div>
    </div>
  );
}

// ─── TierModal ────────────────────────────────────────────────────────────────

function TierModal({
  open,
  tier,
  programId,
  tiersCount,
  onClose,
  onSaved,
}: {
  open: boolean;
  tier?: Tier;
  programId: string;
  tiersCount: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [code, setCode] = useState(tier?.code ?? '');
  const [name, setName] = useState(tier?.name ?? '');
  const [minPoints, setMinPoints] = useState(String(tier?.minPoints ?? 0));
  const [color, setColor] = useState(
    tier?.color ?? DEFAULT_TIER_COLORS[tiersCount % DEFAULT_TIER_COLORS.length] ?? '#71717a',
  );
  const [rank, setRank] = useState(String(tier?.rank ?? tiersCount));

  const mutation = useMutation({
    mutationFn: () =>
      tier
        ? api.patch(`/api/v1/loyalty/programs/${programId}/tiers/${tier.id}`, {
            name,
            minPoints: Number(minPoints),
            color,
            rank: Number(rank),
          })
        : api.post(`/api/v1/loyalty/programs/${programId}/tiers`, {
            code,
            name,
            minPoints: Number(minPoints),
            color,
            rank: Number(rank),
          }),
    onSuccess: () => {
      onSaved();
      toast.success(tier ? 'Tier updated' : 'Tier created');
    },
    onError: (err) => toast.error(extractError(err, 'Error saving tier')),
  });

  return (
    <Modal open={open} onClose={onClose} title={tier ? 'Edit tier' : 'New tier'}>
      <div className="space-y-4">
        {!tier && (
          <Input
            label="Code"
            value={code}
            onChange={(e) => setCode(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
            placeholder="e.g. gold"
          />
        )}
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Gold"
        />
        <Input
          label="Min points"
          type="number"
          min="0"
          value={minPoints}
          onChange={(e) => setMinPoints(e.target.value)}
        />
        <Input
          label="Rank (ascending order)"
          type="number"
          min="0"
          value={rank}
          onChange={(e) => setRank(e.target.value)}
        />
        <div>
          <label className="block text-xs font-medium text-zinc-600 mb-1.5">Badge color</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-10 h-8 rounded border border-zinc-200 cursor-pointer"
            />
            <span className="text-xs font-mono text-zinc-500">{color}</span>
            <div className="flex gap-1 ml-2">
              {DEFAULT_TIER_COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className="w-5 h-5 rounded-full border-2 transition-all"
                  style={{ backgroundColor: c, borderColor: color === c ? c : 'transparent' }}
                />
              ))}
            </div>
          </div>
        </div>
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={mutation.isPending}
            disabled={(!code && !tier) || !name}
            onClick={() => mutation.mutate()}
          >
            {tier ? 'Save' : 'Create tier'}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── RewardModal ──────────────────────────────────────────────────────────────

function RewardModal({
  open,
  reward,
  programId,
  onClose,
  onSaved,
}: {
  open: boolean;
  reward?: Reward;
  programId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(reward?.name ?? '');
  const [description, setDescription] = useState(reward?.description ?? '');
  const [type, setType] = useState(reward?.type ?? 'custom');
  const [pointsCost, setPointsCost] = useState(String(reward?.pointsCost ?? 100));
  const [valueAmount, setValueAmount] = useState(String(reward?.value?.amount ?? 0));
  const [valueUnit, setValueUnit] = useState<'percent' | 'fixed' | 'free'>(
    reward?.value?.unit ?? 'fixed',
  );
  const [availableQuantity, setAvailableQuantity] = useState(
    reward?.availableQuantity != null ? String(reward.availableQuantity) : '',
  );

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        name,
        description: description || undefined,
        type,
        pointsCost: Number(pointsCost),
        value: { amount: Number(valueAmount), unit: valueUnit },
        availableQuantity: availableQuantity ? Number(availableQuantity) : null,
      };
      return reward
        ? api.patch(`/api/v1/loyalty/programs/${programId}/rewards/${reward.id}`, body)
        : api.post(`/api/v1/loyalty/programs/${programId}/rewards`, body);
    },
    onSuccess: () => {
      onSaved();
      toast.success(reward ? 'Reward updated' : 'Reward created');
    },
    onError: (err) => toast.error(extractError(err, 'Error saving reward')),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={reward ? 'Edit reward' : 'New reward'}
      description="Define a reward redeemable with points."
    >
      <div className="space-y-4">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. €10 discount"
        />
        <Textarea
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />
        <Select label="Type" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="custom">Custom</option>
          <option value="discount">Discount</option>
          <option value="free_item">Free item</option>
          <option value="shipping">Free shipping</option>
        </Select>
        <Input
          label="Points cost"
          type="number"
          min="0"
          value={pointsCost}
          onChange={(e) => setPointsCost(e.target.value)}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Value"
            type="number"
            min="0"
            step="0.01"
            value={valueAmount}
            onChange={(e) => setValueAmount(e.target.value)}
          />
          <Select
            label="Unit"
            value={valueUnit}
            onChange={(e) => setValueUnit(e.target.value as typeof valueUnit)}
          >
            <option value="fixed">€ (fixed)</option>
            <option value="percent">% (percent)</option>
            <option value="free">Free</option>
          </Select>
        </div>
        <Input
          label="Available quantity (blank = unlimited)"
          type="number"
          min="1"
          value={availableQuantity}
          onChange={(e) => setAvailableQuantity(e.target.value)}
          placeholder="unlimited"
        />
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={mutation.isPending}
            disabled={!name}
            onClick={() => mutation.mutate()}
          >
            {reward ? 'Save' : 'Create reward'}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── AssignPointsModal ────────────────────────────────────────────────────────

function AssignPointsModal({
  open,
  member,
  programId,
  onClose,
  onSaved,
}: {
  open: boolean;
  member?: Member;
  programId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [points, setPoints] = useState('100');
  const [type, setType] = useState<'earned' | 'adjusted' | 'bonus'>('adjusted');
  const [description, setDescription] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.post('/api/v1/loyalty/points/assign', {
        customerId: member!.customerId,
        programId,
        points: Number(points),
        type,
        description: description || undefined,
      }),
    onSuccess: () => {
      onSaved();
      toast.success('Points assigned');
    },
    onError: (err) => toast.error(extractError(err, 'Error assigning points')),
  });

  if (!member) return null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Assign points"
      description={`Customer: ${fullName(member.firstName, member.lastName, member.email)} — current balance: ${member.pointsBalance.toLocaleString('en')} pt`}
    >
      <div className="space-y-4">
        <Input
          label="Points (negative to deduct)"
          type="number"
          value={points}
          onChange={(e) => setPoints(e.target.value)}
        />
        <Select
          label="Operation type"
          value={type}
          onChange={(e) => setType(e.target.value as typeof type)}
        >
          <option value="adjusted">Adjusted (manual correction)</option>
          <option value="bonus">Bonus</option>
          <option value="earned">Earned</option>
        </Select>
        <Input
          label="Note (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Reason..."
        />
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={mutation.isPending}
            disabled={!points || Number(points) === 0}
            onClick={() => mutation.mutate()}
          >
            Assign points
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── ChangeTierModal ──────────────────────────────────────────────────────────

function ChangeTierModal({
  open,
  member,
  programId,
  tiers,
  onClose,
  onSaved,
}: {
  open: boolean;
  member?: Member;
  programId: string;
  tiers: Tier[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const DEFAULT_TIERS = ['standard', 'silver', 'gold', 'platinum', 'diamond'];
  const tierOptions = tiers.length > 0 ? tiers.map((t) => t.code) : DEFAULT_TIERS;
  const [newTier, setNewTier] = useState(member?.tierCode ?? 'standard');
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.post('/api/v1/loyalty/tiers/change', {
        customerId: member!.customerId,
        programId,
        newTierCode: newTier,
        reason: reason || undefined,
      }),
    onSuccess: () => {
      onSaved();
      toast.success('Tier updated');
    },
    onError: (err) => toast.error(extractError(err, 'Error changing tier')),
  });

  if (!member) return null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Change tier"
      description={`Customer: ${fullName(member.firstName, member.lastName, member.email)} — current tier: ${member.tierCode ?? 'standard'}`}
    >
      <div className="space-y-4">
        <Select label="New tier" value={newTier} onChange={(e) => setNewTier(e.target.value)}>
          {tierOptions.map((t) => {
            const cfg = tiers.find((x) => x.code === t);
            return (
              <option key={t} value={t}>
                {cfg ? `${cfg.name} (${cfg.minPoints.toLocaleString()} pt)` : t}
              </option>
            );
          })}
        </Select>
        <Input
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Manual upgrade for VIP customer"
        />
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" loading={mutation.isPending} onClick={() => mutation.mutate()}>
            Change tier
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── IssueCouponModal ─────────────────────────────────────────────────────────

function IssueCouponModal({
  open,
  member,
  onClose,
  onSaved,
}: {
  open: boolean;
  member?: Member;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [couponType, setCouponType] = useState('discount');
  const [valueAmount, setValueAmount] = useState('10');
  const [valueUnit, setValueUnit] = useState<'percent' | 'fixed' | 'free'>('percent');
  const [expiresInDays, setExpiresInDays] = useState('30');

  const mutation = useMutation({
    mutationFn: () =>
      api.post('/api/v1/loyalty/coupons/issue', {
        customerId: member!.customerId,
        type: couponType,
        value: { amount: Number(valueAmount), unit: valueUnit },
        expiresInDays: expiresInDays ? Number(expiresInDays) : undefined,
      }),
    onSuccess: () => {
      onSaved();
      toast.success('Coupon issued');
    },
    onError: (err) => toast.error(extractError(err, 'Error issuing coupon')),
  });

  if (!member) return null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Issue coupon"
      description={`Recipient: ${fullName(member.firstName, member.lastName, member.email)}`}
    >
      <div className="space-y-4">
        <Select
          label="Coupon type"
          value={couponType}
          onChange={(e) => setCouponType(e.target.value)}
        >
          <option value="discount">Discount</option>
          <option value="free_item">Free item</option>
          <option value="shipping">Free shipping</option>
          <option value="custom">Custom</option>
        </Select>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Value"
            type="number"
            min="0"
            step="0.01"
            value={valueAmount}
            onChange={(e) => setValueAmount(e.target.value)}
          />
          <Select
            label="Unit"
            value={valueUnit}
            onChange={(e) => setValueUnit(e.target.value as typeof valueUnit)}
          >
            <option value="percent">%</option>
            <option value="fixed">€ (fixed)</option>
            <option value="free">Free</option>
          </Select>
        </div>
        <Input
          label="Expires in (days, blank = never)"
          type="number"
          min="1"
          value={expiresInDays}
          onChange={(e) => setExpiresInDays(e.target.value)}
          placeholder="30"
        />
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" loading={mutation.isPending} onClick={() => mutation.mutate()}>
            Issue coupon
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── RedeemRewardModal ────────────────────────────────────────────────────────

function RedeemRewardModal({
  open,
  member,
  programId,
  rewards,
  onClose,
  onSaved,
}: {
  open: boolean;
  member?: Member;
  programId: string;
  rewards: Reward[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const activeRewards = rewards.filter(
    (r) => r.isActive && (r.availableQuantity === null || r.redeemedCount < r.availableQuantity),
  );
  const [selectedRewardId, setSelectedRewardId] = useState('');
  const selectedReward = activeRewards.find((r) => r.id === selectedRewardId);

  const mutation = useMutation({
    mutationFn: () =>
      api.post('/api/v1/loyalty/rewards/redeem', {
        customerId: member!.customerId,
        programId,
        rewardId: selectedRewardId,
        pointsCost: selectedReward!.pointsCost,
      }),
    onSuccess: () => {
      onSaved();
      toast.success('Reward redeemed');
    },
    onError: (err) => toast.error(extractError(err, 'Error redeeming reward')),
  });

  if (!member) return null;
  const canAfford = selectedReward ? member.pointsBalance >= selectedReward.pointsCost : false;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Redeem reward"
      description={`Customer: ${fullName(member.firstName, member.lastName, member.email)} — balance: ${member.pointsBalance.toLocaleString('en')} pt`}
    >
      <div className="space-y-4">
        {activeRewards.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-4">No active rewards in catalog.</p>
        ) : (
          <>
            <Select
              label="Select reward"
              value={selectedRewardId}
              onChange={(e) => setSelectedRewardId(e.target.value)}
            >
              <option value="">— choose a reward —</option>
              {activeRewards.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} — {r.pointsCost.toLocaleString('en')} pt
                </option>
              ))}
            </Select>
            {selectedReward && !canAfford && (
              <p className="text-xs text-red-600">
                Insufficient points: {selectedReward.pointsCost.toLocaleString('en')} pt required,
                customer has {member.pointsBalance.toLocaleString('en')}.
              </p>
            )}
          </>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={mutation.isPending}
            disabled={!selectedRewardId || !canAfford}
            onClick={() => mutation.mutate()}
          >
            Redeem
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── PointRulesTab ────────────────────────────────────────────────────────────

const POINT_RULE_TYPE_LABELS: Record<string, string> = {
  page_view: 'Page view',
  article_read: 'Article read',
  video_watched: 'Video watched',
  social_share: 'Social share',
  comment_posted: 'Comment posted',
  quiz_completed: 'Quiz completed',
  poll_voted: 'Poll voted',
  daily_login: 'Daily login',
  streak_bonus: 'Streak bonus',
  referral_signup: 'Referral signup',
  contest_play: 'Contest play',
};

function PointRulesTab({ programId }: { programId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [modal, setModal] = useState<{ open: boolean; rule?: PointRule }>({ open: false });
  const [deleteTarget, setDeleteTarget] = useState<PointRule | null>(null);

  const { data: rules = [], isLoading } = useQuery<PointRule[]>({
    queryKey: ['loyalty-point-rules', programId],
    queryFn: () => api.get('/api/v1/loyalty/point-rules', { params: { programId } }).then((r) => r.data),
    enabled: !!programId,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ ruleId, isActive }: { ruleId: string; isActive: boolean }) =>
      api.patch(`/api/v1/loyalty/point-rules/${ruleId}`, { isActive }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-point-rules', programId] });
      toast.success('Rule updated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (ruleId: string) =>
      api.delete(`/api/v1/loyalty/point-rules/${ruleId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-point-rules', programId] });
      setDeleteTarget(null);
      toast.info('Rule deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-zinc-500">
          Rules that award points when customers perform specific actions.
        </p>
        <Button size="sm" onClick={() => setModal({ open: true })}>
          <Plus size={13} className="mr-1.5" /> Add rule
        </Button>
      </div>
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100">
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Rule type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Points
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Daily cap
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Streak threshold
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 w-24" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-50">
            {isLoading ? (
              <SkeletonRows count={4} cols={6} />
            ) : rules.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <EmptyState
                    icon={<Zap size={24} />}
                    title="No point rules"
                    description="Define how customers earn points for their actions."
                    action={{ label: '+ Add rule', onClick: () => setModal({ open: true }) }}
                  />
                </td>
              </tr>
            ) : (
              rules.map((r) => (
                <tr key={r.id} className="group hover:bg-zinc-50/60 transition-colors">
                  <td className="px-4 py-3">
                    <span className="font-medium text-zinc-900">
                      {POINT_RULE_TYPE_LABELS[r.ruleType] ?? r.ruleType}
                    </span>
                    <code className="ml-2 text-[10px] bg-zinc-100 text-zinc-500 px-1 py-0.5 rounded">
                      {r.ruleType}
                    </code>
                  </td>
                  <td className="px-4 py-3 font-semibold text-emerald-700 tabular-nums">
                    +{r.points}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500">
                    {r.dailyCap != null ? (
                      `${r.dailyCap}×/day`
                    ) : (
                      <span className="text-zinc-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500">
                    {r.streakThreshold != null ? (
                      `${r.streakThreshold} days`
                    ) : (
                      <span className="text-zinc-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Badge variant={r.isActive ? 'success' : 'default'}>
                        {r.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      {r.contributesToLeaderboard && <Badge variant="info">Leaderboard</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setModal({ open: true, rule: r })}
                        className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                      >
                        <Pencil size={11} />
                      </button>
                      <button
                        onClick={() =>
                          toggleMutation.mutate({ ruleId: r.id, isActive: !r.isActive })
                        }
                        className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                        title={r.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {r.isActive ? (
                          <ToggleRight size={13} className="text-emerald-500" />
                        ) : (
                          <ToggleLeft size={13} />
                        )}
                      </button>
                      <button
                        onClick={() => setDeleteTarget(r)}
                        className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PointRuleModal
        key={modal.rule?.id ?? 'new-rule'}
        open={modal.open}
        rule={modal.rule}
        programId={programId}
        onClose={() => setModal({ open: false })}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['loyalty-point-rules', programId] });
          setModal({ open: false });
        }}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete point rule?"
        message={`Rule "${deleteTarget ? (POINT_RULE_TYPE_LABELS[deleteTarget.ruleType] ?? deleteTarget.ruleType) : ''}" will be permanently deleted.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function PointRuleModal({
  open,
  rule,
  programId,
  onClose,
  onSaved,
}: {
  open: boolean;
  rule?: PointRule;
  programId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [ruleType, setRuleType] = useState(rule?.ruleType ?? 'page_view');
  const [points, setPoints] = useState(String(rule?.points ?? 5));
  const [dailyCap, setDailyCap] = useState(rule?.dailyCap != null ? String(rule.dailyCap) : '');
  const [streakThreshold, setStreakThreshold] = useState(
    rule?.streakThreshold != null ? String(rule.streakThreshold) : '',
  );
  const [minTimeOnPage, setMinTimeOnPage] = useState(
    rule?.minTimeOnPageSeconds != null ? String(rule.minTimeOnPageSeconds) : '',
  );
  const [contributesToLeaderboard, setContributesToLeaderboard] = useState(
    rule?.contributesToLeaderboard ?? false,
  );

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        ruleType,
        points: Number(points),
        dailyCap: dailyCap ? Number(dailyCap) : null,
        streakThreshold: streakThreshold ? Number(streakThreshold) : null,
        minTimeOnPageSeconds: minTimeOnPage ? Number(minTimeOnPage) : null,
        contributesToLeaderboard,
      };
      return rule
        ? api.patch(`/api/v1/loyalty/point-rules/${rule.id}`, body)
        : api.post('/api/v1/loyalty/point-rules', { ...body, programId });
    },
    onSuccess: () => {
      onSaved();
      toast.success(rule ? 'Rule updated' : 'Rule created');
    },
    onError: (err) => toast.error(extractError(err, 'Error saving rule')),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={rule ? 'Edit point rule' : 'New point rule'}
      description="Configure how many points this action awards."
    >
      <div className="space-y-4">
        <Select
          label="Rule type"
          value={ruleType}
          onChange={(e) => setRuleType(e.target.value)}
          disabled={!!rule}
        >
          {Object.entries(POINT_RULE_TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
        <Input
          label="Points awarded"
          type="number"
          min="1"
          value={points}
          onChange={(e) => setPoints(e.target.value)}
        />
        <div>
          <Input
            label="Daily cap (blank = unlimited)"
            type="number"
            min="1"
            value={dailyCap}
            onChange={(e) => setDailyCap(e.target.value)}
            placeholder="e.g. 3"
          />
          <p className="mt-1 text-[11px] text-zinc-400">
            Max times this rule can trigger per customer per day
          </p>
        </div>
        <div>
          <Input
            label="Streak threshold (days)"
            type="number"
            min="1"
            value={streakThreshold}
            onChange={(e) => setStreakThreshold(e.target.value)}
            placeholder="e.g. 7"
          />
          <p className="mt-1 text-[11px] text-zinc-400">Only applies for streak_bonus rule type</p>
        </div>
        <div>
          <Input
            label="Min. time on page (seconds)"
            type="number"
            min="1"
            value={minTimeOnPage}
            onChange={(e) => setMinTimeOnPage(e.target.value)}
            placeholder="e.g. 30"
          />
          <p className="mt-1 text-[11px] text-zinc-400">
            Only applies for article_read / page_view rule types
          </p>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-zinc-200 bg-zinc-50 p-3">
          <input
            id="contributes-leaderboard"
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
            checked={contributesToLeaderboard}
            onChange={(e) => setContributesToLeaderboard(e.target.checked)}
          />
          <div>
            <label
              htmlFor="contributes-leaderboard"
              className="block text-sm font-medium text-zinc-700 cursor-pointer"
            >
              Contributes to leaderboard
            </label>
            <p className="mt-0.5 text-[11px] text-zinc-400">
              When enabled, points from this rule are added to the customer's leaderboard score
              (monthly &amp; weekly rankings).
            </p>
          </div>
        </div>
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={mutation.isPending}
            disabled={!points || Number(points) < 1}
            onClick={() => mutation.mutate()}
          >
            {rule ? 'Save' : 'Create rule'}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── BadgesTab ────────────────────────────────────────────────────────────────

const BADGE_CONDITION_LABELS: Record<string, string> = {
  points_total: 'Total points',
  streak_days: 'Streak days',
  articles_read: 'Articles read',
  contests_played: 'Contests played',
  referrals_completed: 'Referrals completed',
  badges_count: 'Badges collected',
};
const BADGE_BENEFIT_LABELS: Record<string, string> = {
  none: 'None',
  point_multiplier: 'Point multiplier',
  exclusive_content: 'Exclusive content',
  premium_access: 'Premium access',
};

function BadgesTab({ programId }: { programId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [modal, setModal] = useState<{ open: boolean; badge?: BadgeDefinition }>({ open: false });
  const [deleteTarget, setDeleteTarget] = useState<BadgeDefinition | null>(null);

  const { data: badges = [], isLoading } = useQuery<BadgeDefinition[]>({
    queryKey: ['loyalty-badges', programId],
    queryFn: () => api.get('/api/v1/loyalty/badges', { params: { programId } }).then((r) => r.data),
    enabled: !!programId,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ badgeId, isActive }: { badgeId: string; isActive: boolean }) =>
      api.patch(`/api/v1/loyalty/badges/${badgeId}`, { isActive }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-badges', programId] });
      toast.success('Badge updated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (badgeId: string) =>
      api.delete(`/api/v1/loyalty/badges/${badgeId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-badges', programId] });
      setDeleteTarget(null);
      toast.info('Badge deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-zinc-500">
          Badges are unlocked when customers reach specific milestones and may grant benefits.
        </p>
        <Button size="sm" onClick={() => setModal({ open: true })}>
          <Plus size={13} className="mr-1.5" /> Add badge
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-3">
            <SkeletonRows count={3} cols={1} />
          </div>
        ) : badges.length === 0 ? (
          <div className="col-span-3">
            <EmptyState
              icon={<Award size={24} />}
              title="No badges defined"
              description="Create badges that customers can unlock by reaching milestones."
              action={{ label: '+ Add badge', onClick: () => setModal({ open: true }) }}
            />
          </div>
        ) : (
          badges.map((b) => (
            <div
              key={b.id}
              className="group bg-white border border-zinc-200 rounded-xl p-4 relative"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  {b.iconUrl ? (
                    <img src={b.iconUrl} alt={b.name} className="w-6 h-6 object-contain" />
                  ) : (
                    <Award size={18} className="text-amber-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="font-semibold text-zinc-900 text-sm">{b.name}</p>
                    <Badge variant={b.isActive ? 'success' : 'default'} className="text-[10px]">
                      {b.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  {b.description && (
                    <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{b.description}</p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-zinc-500">
                    <span>
                      <span className="font-medium text-zinc-700">Condition:</span>{' '}
                      {BADGE_CONDITION_LABELS[b.conditionType] ?? b.conditionType} ≥{' '}
                      {b.conditionThreshold}
                    </span>
                    {b.benefitType !== 'none' && (
                      <span>
                        <span className="font-medium text-zinc-700">Benefit:</span>{' '}
                        {BADGE_BENEFIT_LABELS[b.benefitType] ?? b.benefitType}
                        {b.benefitValue ? ` (${b.benefitValue})` : ''}
                        {b.benefitDurationDays ? ` · ${b.benefitDurationDays}d` : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="absolute top-3 right-3 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => setModal({ open: true, badge: b })}
                  className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                >
                  <Pencil size={11} />
                </button>
                <button
                  onClick={() => toggleMutation.mutate({ badgeId: b.id, isActive: !b.isActive })}
                  className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                >
                  {b.isActive ? (
                    <ToggleRight size={13} className="text-emerald-500" />
                  ) : (
                    <ToggleLeft size={13} />
                  )}
                </button>
                <button
                  onClick={() => setDeleteTarget(b)}
                  className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <BadgeModal
        key={modal.badge?.id ?? 'new-badge'}
        open={modal.open}
        badge={modal.badge}
        programId={programId}
        onClose={() => setModal({ open: false })}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['loyalty-badges', programId] });
          setModal({ open: false });
        }}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete badge?"
        message={`Badge "${deleteTarget?.name}" will be permanently deleted. Customers who already earned it will keep their record.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function BadgeModal({
  open,
  badge,
  programId,
  onClose,
  onSaved,
}: {
  open: boolean;
  badge?: BadgeDefinition;
  programId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [code, setCode] = useState(badge?.code ?? '');
  const [name, setName] = useState(badge?.name ?? '');
  const [description, setDescription] = useState(badge?.description ?? '');
  const [iconUrl, setIconUrl] = useState(badge?.iconUrl ?? '');
  const [conditionType, setConditionType] = useState(badge?.conditionType ?? 'points_total');
  const [conditionThreshold, setConditionThreshold] = useState(
    String(badge?.conditionThreshold ?? 100),
  );
  const [benefitType, setBenefitType] = useState(badge?.benefitType ?? 'none');
  const [benefitValue, setBenefitValue] = useState(badge?.benefitValue ?? '');
  const [benefitDurationDays, setBenefitDurationDays] = useState(
    badge?.benefitDurationDays != null ? String(badge.benefitDurationDays) : '',
  );
  const [displayOrder, setDisplayOrder] = useState(String(badge?.displayOrder ?? 0));

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        code: badge ? undefined : code,
        name,
        description: description || null,
        iconUrl: iconUrl || null,
        conditionType,
        conditionThreshold: Number(conditionThreshold),
        benefitType,
        benefitValue: benefitValue || null,
        benefitDurationDays: benefitDurationDays ? Number(benefitDurationDays) : null,
        displayOrder: Number(displayOrder),
      };
      return badge
        ? api.patch(`/api/v1/loyalty/badges/${badge.id}`, body)
        : api.post('/api/v1/loyalty/badges', { ...body, programId });
    },
    onSuccess: () => {
      onSaved();
      toast.success(badge ? 'Badge updated' : 'Badge created');
    },
    onError: (err) => toast.error(extractError(err, 'Error saving badge')),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={badge ? 'Edit badge' : 'New badge'}
      description="Define a milestone badge customers can unlock."
    >
      <div className="space-y-4">
        {!badge && (
          <Input
            label="Code (unique identifier)"
            value={code}
            onChange={(e) => setCode(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
            placeholder="e.g. reader_100"
          />
        )}
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Avid Reader"
        />
        <Textarea
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />
        <Input
          label="Icon URL (optional)"
          value={iconUrl}
          onChange={(e) => setIconUrl(e.target.value)}
          placeholder="https://..."
        />
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Condition type"
            value={conditionType}
            onChange={(e) => setConditionType(e.target.value)}
          >
            {Object.entries(BADGE_CONDITION_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
          <Input
            label="Threshold"
            type="number"
            min="1"
            value={conditionThreshold}
            onChange={(e) => setConditionThreshold(e.target.value)}
          />
        </div>
        <Select
          label="Benefit type"
          value={benefitType}
          onChange={(e) => setBenefitType(e.target.value)}
        >
          {Object.entries(BADGE_BENEFIT_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
        {benefitType !== 'none' && (
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Benefit value"
              value={benefitValue}
              onChange={(e) => setBenefitValue(e.target.value)}
              placeholder="e.g. 1.5 (for multiplier)"
            />
            <Input
              label="Duration (days, blank = permanent)"
              type="number"
              min="1"
              value={benefitDurationDays}
              onChange={(e) => setBenefitDurationDays(e.target.value)}
            />
          </div>
        )}
        <Input
          label="Display order"
          type="number"
          min="0"
          value={displayOrder}
          onChange={(e) => setDisplayOrder(e.target.value)}
        />
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={mutation.isPending}
            disabled={(!code && !badge) || !name}
            onClick={() => mutation.mutate()}
          >
            {badge ? 'Save' : 'Create badge'}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── InterestsTab ─────────────────────────────────────────────────────────────

function InterestsTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const [categoryModal, setCategoryModal] = useState<{
    open: boolean;
    category?: InterestCategory;
  }>({ open: false });
  const [topicModal, setTopicModal] = useState<{
    open: boolean;
    categoryId?: string;
    topic?: InterestTopic;
  }>({ open: false });
  const [deleteCategoryTarget, setDeleteCategoryTarget] = useState<InterestCategory | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const { data: categories = [], isLoading } = useQuery<InterestCategory[]>({
    queryKey: ['loyalty-interest-categories'],
    queryFn: () => api.get('/api/v1/loyalty/interests/categories').then((r) => r.data),
  });

  const toggleCategoryMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/api/v1/loyalty/interests/categories/${id}`, { isActive }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-interest-categories'] });
      toast.success('Category updated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/loyalty/interests/categories/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-interest-categories'] });
      setDeleteCategoryTarget(null);
      toast.info('Category deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const toggleTopicMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/api/v1/loyalty/interests/topics/${id}`, { isActive }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-interest-categories'] });
      toast.success('Topic updated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-zinc-500">
          Interest categories and topics customers can subscribe to for personalised content.
        </p>
        <Button size="sm" onClick={() => setCategoryModal({ open: true })}>
          <Plus size={13} className="mr-1.5" /> Add category
        </Button>
      </div>

      {isLoading ? (
        <SkeletonRows count={4} cols={1} />
      ) : categories.length === 0 ? (
        <EmptyState
          icon={<Tag size={24} />}
          title="No interest categories"
          description="Create categories and topics to let customers express their preferences."
          action={{ label: '+ Add category', onClick: () => setCategoryModal({ open: true }) }}
        />
      ) : (
        <div className="space-y-3">
          {categories.map((cat) => (
            <div
              key={cat.id}
              className="bg-white border border-zinc-200 rounded-xl overflow-hidden"
            >
              {/* Category row */}
              <div className="flex items-center gap-3 px-4 py-3">
                <button
                  onClick={() => setExpandedCategory(expandedCategory === cat.id ? null : cat.id)}
                  className="p-0.5 text-zinc-400 hover:text-zinc-700 transition-colors"
                >
                  <ChevronRight
                    size={14}
                    className={`transition-transform ${expandedCategory === cat.id ? 'rotate-90' : ''}`}
                  />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-zinc-900 text-sm">{cat.name}</span>
                    <code className="text-[10px] bg-zinc-100 text-zinc-500 px-1 py-0.5 rounded">
                      {cat.code}
                    </code>
                    <Badge variant={cat.isActive ? 'success' : 'default'} className="text-[10px]">
                      {cat.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    <span className="text-xs text-zinc-400">{cat.topics.length} topics</span>
                  </div>
                  {cat.description && (
                    <p className="text-xs text-zinc-400 mt-0.5 truncate">{cat.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setTopicModal({ open: true, categoryId: cat.id })}
                    className="px-2 py-1 text-xs font-medium text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded-md transition-colors"
                  >
                    + Topic
                  </button>
                  <button
                    onClick={() => setCategoryModal({ open: true, category: cat })}
                    className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                  >
                    <Pencil size={11} />
                  </button>
                  <button
                    onClick={() =>
                      toggleCategoryMutation.mutate({ id: cat.id, isActive: !cat.isActive })
                    }
                    className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                  >
                    {cat.isActive ? (
                      <ToggleRight size={13} className="text-emerald-500" />
                    ) : (
                      <ToggleLeft size={13} />
                    )}
                  </button>
                  <button
                    onClick={() => setDeleteCategoryTarget(cat)}
                    className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>

              {/* Topics list */}
              {expandedCategory === cat.id && cat.topics.length > 0 && (
                <div className="border-t border-zinc-100 divide-y divide-zinc-50">
                  {cat.topics.map((topic) => (
                    <div
                      key={topic.id}
                      className="group flex items-center gap-3 pl-10 pr-4 py-2.5 hover:bg-zinc-50/60 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-zinc-800">{topic.name}</span>
                          <code className="text-[10px] bg-zinc-100 text-zinc-500 px-1 py-0.5 rounded">
                            {topic.code}
                          </code>
                          <Badge
                            variant={topic.isActive ? 'success' : 'default'}
                            className="text-[10px]"
                          >
                            {topic.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setTopicModal({ open: true, categoryId: cat.id, topic })}
                          className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                        >
                          <Pencil size={11} />
                        </button>
                        <button
                          onClick={() =>
                            toggleTopicMutation.mutate({ id: topic.id, isActive: !topic.isActive })
                          }
                          className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                        >
                          {topic.isActive ? (
                            <ToggleRight size={13} className="text-emerald-500" />
                          ) : (
                            <ToggleLeft size={13} />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {expandedCategory === cat.id && cat.topics.length === 0 && (
                <div className="border-t border-zinc-100 px-10 py-4 text-xs text-zinc-400">
                  No topics yet.{' '}
                  <button
                    onClick={() => setTopicModal({ open: true, categoryId: cat.id })}
                    className="underline hover:text-zinc-700"
                  >
                    Add the first one
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Category modal */}
      <InterestCategoryModal
        key={categoryModal.category?.id ?? 'new-cat'}
        open={categoryModal.open}
        category={categoryModal.category}
        onClose={() => setCategoryModal({ open: false })}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['loyalty-interest-categories'] });
          setCategoryModal({ open: false });
        }}
      />

      {/* Topic modal */}
      <InterestTopicModal
        key={topicModal.topic?.id ?? `new-topic-${topicModal.categoryId}`}
        open={topicModal.open}
        categoryId={topicModal.categoryId}
        topic={topicModal.topic}
        onClose={() => setTopicModal({ open: false })}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['loyalty-interest-categories'] });
          setTopicModal({ open: false });
        }}
      />

      <ConfirmDialog
        open={!!deleteCategoryTarget}
        title="Delete category?"
        message={`Category "${deleteCategoryTarget?.name}" and all its topics will be permanently deleted.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteCategoryMutation.isPending}
        onConfirm={() =>
          deleteCategoryTarget && deleteCategoryMutation.mutate(deleteCategoryTarget.id)
        }
        onCancel={() => setDeleteCategoryTarget(null)}
      />
    </div>
  );
}

function InterestCategoryModal({
  open,
  category,
  onClose,
  onSaved,
}: {
  open: boolean;
  category?: InterestCategory;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [code, setCode] = useState(category?.code ?? '');
  const [name, setName] = useState(category?.name ?? '');
  const [description, setDescription] = useState(category?.description ?? '');
  const [iconUrl, setIconUrl] = useState(category?.iconUrl ?? '');
  const [displayOrder, setDisplayOrder] = useState(String(category?.displayOrder ?? 0));

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        code: category ? undefined : code,
        name,
        description: description || null,
        iconUrl: iconUrl || null,
        displayOrder: Number(displayOrder),
      };
      return category
        ? api.patch(`/api/v1/loyalty/interests/categories/${category.id}`, body)
        : api.post('/api/v1/loyalty/interests/categories', body);
    },
    onSuccess: () => {
      onSaved();
      toast.success(category ? 'Category updated' : 'Category created');
    },
    onError: (err) => toast.error(extractError(err, 'Error saving category')),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={category ? 'Edit category' : 'New interest category'}
    >
      <div className="space-y-4">
        {!category && (
          <Input
            label="Code"
            value={code}
            onChange={(e) => setCode(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
            placeholder="e.g. sports"
          />
        )}
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Sports"
        />
        <Textarea
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />
        <Input
          label="Icon URL (optional)"
          value={iconUrl}
          onChange={(e) => setIconUrl(e.target.value)}
          placeholder="https://..."
        />
        <Input
          label="Display order"
          type="number"
          min="0"
          value={displayOrder}
          onChange={(e) => setDisplayOrder(e.target.value)}
        />
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={mutation.isPending}
            disabled={(!code && !category) || !name}
            onClick={() => mutation.mutate()}
          >
            {category ? 'Save' : 'Create category'}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

function InterestTopicModal({
  open,
  categoryId,
  topic,
  onClose,
  onSaved,
}: {
  open: boolean;
  categoryId?: string;
  topic?: InterestTopic;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [code, setCode] = useState(topic?.code ?? '');
  const [name, setName] = useState(topic?.name ?? '');
  const [iconUrl, setIconUrl] = useState(topic?.iconUrl ?? '');
  const [displayOrder, setDisplayOrder] = useState(String(topic?.displayOrder ?? 0));

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        code: topic ? undefined : code,
        name,
        iconUrl: iconUrl || null,
        displayOrder: Number(displayOrder),
      };
      return topic
        ? api.patch(`/api/v1/loyalty/interests/topics/${topic.id}`, body)
        : api.post(`/api/v1/loyalty/interests/categories/${categoryId}/topics`, body);
    },
    onSuccess: () => {
      onSaved();
      toast.success(topic ? 'Topic updated' : 'Topic created');
    },
    onError: (err) => toast.error(extractError(err, 'Error saving topic')),
  });

  return (
    <Modal open={open} onClose={onClose} title={topic ? 'Edit topic' : 'New topic'}>
      <div className="space-y-4">
        {!topic && (
          <Input
            label="Code"
            value={code}
            onChange={(e) => setCode(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
            placeholder="e.g. football"
          />
        )}
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Football"
        />
        <Input
          label="Icon URL (optional)"
          value={iconUrl}
          onChange={(e) => setIconUrl(e.target.value)}
          placeholder="https://..."
        />
        <Input
          label="Display order"
          type="number"
          min="0"
          value={displayOrder}
          onChange={(e) => setDisplayOrder(e.target.value)}
        />
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={mutation.isPending}
            disabled={(!code && !topic) || !name}
            onClick={() => mutation.mutate()}
          >
            {topic ? 'Save' : 'Create topic'}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── ContestsTab ──────────────────────────────────────────────────────────────

const CONTEST_STATUS_COLORS: Record<string, string> = {
  draft: 'bg-zinc-100 text-zinc-500',
  active: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-amber-100 text-amber-700',
  completed: 'bg-sky-100 text-sky-700',
  cancelled: 'bg-red-100 text-red-600',
};

const CONTEST_TYPE_COLORS: Record<string, string> = {
  instant_win: 'bg-purple-100 text-purple-700',
  draw: 'bg-sky-100 text-sky-700',
  leaderboard: 'bg-amber-100 text-amber-700',
};

const CONTEST_TYPE_LABELS: Record<string, string> = {
  instant_win: 'Instant Win',
  draw: 'Draw',
  leaderboard: 'Leaderboard',
};

function ContestsTab({ programId }: { programId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('');
  const [contestModal, setContestModal] = useState<{ open: boolean; contest?: Contest }>({
    open: false,
  });
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: contests = [], isLoading } = useQuery<Contest[]>({
    queryKey: ['loyalty-contests', programId, statusFilter],
    queryFn: () =>
      api
        .get('/api/v1/loyalty/contests', {
          params: { programId, ...(statusFilter ? { status: statusFilter } : {}) },
        })
        .then((r) => r.data),
    enabled: !!programId,
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/loyalty/contests/${id}/activate`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-contests', programId] });
      toast.success('Contest activated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const pauseMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/loyalty/contests/${id}/pause`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-contests', programId] });
      toast.info('Contest paused');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/loyalty/contests/${id}/complete`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-contests', programId] });
      toast.info('Contest completed');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <p className="text-sm text-zinc-500">Manage contests for this loyalty program.</p>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs border border-zinc-200 rounded-lg px-2 py-1.5 bg-white text-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-300"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <Button size="sm" onClick={() => setContestModal({ open: true })}>
          <Plus size={13} className="mr-1.5" /> New contest
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100">
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Title
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Period
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Validation
              </th>
              <th className="px-4 py-3 w-32" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-50">
            {isLoading ? (
              <SkeletonRows count={3} cols={6} />
            ) : contests.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <EmptyState
                    icon={<Trophy size={24} />}
                    title="No contests yet"
                    description="Create your first contest for this loyalty program."
                    action={{
                      label: '+ New contest',
                      onClick: () => setContestModal({ open: true }),
                    }}
                  />
                </td>
              </tr>
            ) : (
              contests.map((c) => (
                <React.Fragment key={c.id}>
                  <tr
                    className="group hover:bg-zinc-50/60 transition-colors cursor-pointer"
                    onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-300 transition-transform">
                          {expandedId === c.id ? (
                            <ChevronDown size={13} />
                          ) : (
                            <ChevronRight size={13} />
                          )}
                        </span>
                        <div>
                          <p className="font-medium text-zinc-900">{c.title}</p>
                          {c.description && (
                            <p className="text-xs text-zinc-400 truncate max-w-[200px]">
                              {c.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${CONTEST_TYPE_COLORS[c.contestType] ?? 'bg-zinc-100 text-zinc-500'}`}
                      >
                        {CONTEST_TYPE_LABELS[c.contestType] ?? c.contestType}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full capitalize ${CONTEST_STATUS_COLORS[c.status] ?? 'bg-zinc-100 text-zinc-500'}`}
                      >
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500">
                      {fmt(c.startDate)} — {fmt(c.endDate)}
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500 capitalize">
                      {c.winValidation}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {c.status === 'draft' && (
                          <button
                            onClick={() => activateMutation.mutate(c.id)}
                            title="Activate"
                            className="p-1.5 text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-md"
                          >
                            <Play size={11} />
                          </button>
                        )}
                        {c.status === 'active' && (
                          <button
                            onClick={() => pauseMutation.mutate(c.id)}
                            title="Pause"
                            className="p-1.5 text-zinc-400 hover:text-amber-600 hover:bg-amber-50 rounded-md"
                          >
                            <PauseIcon size={11} />
                          </button>
                        )}
                        {c.status === 'paused' && (
                          <button
                            onClick={() => activateMutation.mutate(c.id)}
                            title="Resume"
                            className="p-1.5 text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-md"
                          >
                            <Play size={11} />
                          </button>
                        )}
                        {(c.status === 'active' || c.status === 'paused') && (
                          <button
                            onClick={() => completeMutation.mutate(c.id)}
                            title="Complete"
                            className="p-1.5 text-zinc-400 hover:text-sky-600 hover:bg-sky-50 rounded-md"
                          >
                            <Check size={11} />
                          </button>
                        )}
                        <button
                          onClick={() => setContestModal({ open: true, contest: c })}
                          title="Edit"
                          className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                        >
                          <Pencil size={11} />
                        </button>
                        <Link
                          to={`/loyalty/${programId}/contests/${c.id}/test-arena`}
                          title="Test Arena"
                          className="p-1.5 text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-md"
                        >
                          <Gamepad2 size={11} />
                        </Link>
                      </div>
                    </td>
                  </tr>
                  {expandedId === c.id && (
                    <tr key={`${c.id}-detail`}>
                      <td colSpan={6} className="bg-zinc-50 border-b border-zinc-100 px-6 py-4">
                        <ContestRoundsPanel contestId={c.id} contestType={c.contestType} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ContestModal
        key={contestModal.contest?.id ?? 'new-contest'}
        open={contestModal.open}
        contest={contestModal.contest}
        programId={programId}
        onClose={() => setContestModal({ open: false })}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['loyalty-contests', programId] });
          setContestModal({ open: false });
        }}
      />
    </div>
  );
}

// ─── PrizeSlotList ────────────────────────────────────────────────────────────

const PRIZE_STATUS_COLORS: Record<string, string> = {
  available: 'bg-emerald-100 text-emerald-700',
  won: 'bg-violet-100 text-violet-700',
  expired: 'bg-zinc-100 text-zinc-500',
  recovered: 'bg-amber-100 text-amber-700',
};

function PrizeSlotList({ contestId, roundId }: { contestId: string; roundId: string }) {
  const { data: prizes = [], isLoading } = useQuery<ContestPrize[]>({
    queryKey: ['contest-prizes', contestId, roundId],
    queryFn: () =>
      api.get(`/api/v1/loyalty/contests/${contestId}/rounds/${roundId}/prizes`).then((r) => r.data),
  });

  if (isLoading) return <div className="text-xs text-zinc-400 py-2 px-1">Loading prizes…</div>;
  if (prizes.length === 0) return <div className="text-xs text-zinc-400 py-2 px-1">No prizes in this round.</div>;

  return (
    <div className="mt-2 flex flex-wrap gap-1.5 px-1 pb-1">
      {prizes.map((p) => (
        <span
          key={p.id}
          title={`${p.name}${p.winnerId ? ` — won by ${p.winnerId}` : ''}`}
          className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full ${PRIZE_STATUS_COLORS[p.status] ?? 'bg-zinc-100 text-zinc-500'}`}
        >
          {p.name}
          {p.isRecovered && <span title="Recovered from previous round">↩</span>}
        </span>
      ))}
    </div>
  );
}

// ─── ValidationQueue ──────────────────────────────────────────────────────────

function ValidationQueue({ contestId }: { contestId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: prizes = [], isLoading } = useQuery<ContestPrize[]>({
    queryKey: ['contest-prizes-pending', contestId],
    queryFn: () =>
      api
        .get(`/api/v1/loyalty/prizes/pending-validation?contestId=${contestId}`)
        .then((r) => r.data),
  });

  const validateMutation = useMutation({
    mutationFn: ({ prizeId, status, note }: { prizeId: string; status: 'confirmed' | 'rejected'; note?: string }) =>
      api.patch(`/api/v1/loyalty/prizes/${prizeId}/validate`, { status, note }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contest-prizes-pending', contestId] });
      toast.success('Prize validated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  if (isLoading) return <div className="text-xs text-zinc-400 py-4">Loading validation queue…</div>;
  if (prizes.length === 0)
    return (
      <div className="text-xs text-zinc-400 py-4 text-center">No prizes pending validation.</div>
    );

  return (
    <div className="space-y-2">
      {prizes.map((p) => (
        <div
          key={p.id}
          className="flex items-center justify-between p-3 bg-white border border-zinc-200 rounded-lg"
        >
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-zinc-800 truncate">{p.name}</p>
            <p className="text-xs text-zinc-400">
              Winner: {p.winnerId ?? '—'} · Won:{' '}
              {p.wonAt ? new Date(p.wonAt as string).toLocaleDateString() : '—'}
            </p>
          </div>
          <div className="flex items-center gap-2 ml-3 shrink-0">
            <Button
              size="sm"
              variant="ghost"
              loading={validateMutation.isPending}
              onClick={() =>
                validateMutation.mutate({ prizeId: p.id, status: 'confirmed' })
              }
              className="text-emerald-600 hover:text-emerald-700"
            >
              <Check size={12} className="mr-1" /> Confirm
            </Button>
            <Button
              size="sm"
              variant="ghost"
              loading={validateMutation.isPending}
              onClick={() =>
                validateMutation.mutate({ prizeId: p.id, status: 'rejected' })
              }
              className="text-red-500 hover:text-red-600"
            >
              ✕ Reject
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── ContestRoundsPanel ───────────────────────────────────────────────────────

function ContestRoundsPanel({
  contestId,
  contestType,
}: {
  contestId: string;
  contestType: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [addRoundOpen, setAddRoundOpen] = useState(false);
  const [roundName, setRoundName] = useState('');
  const [roundStart, setRoundStart] = useState('');
  const [roundEnd, setRoundEnd] = useState('');
  const [bulkModal, setBulkModal] = useState<{ open: boolean; roundId?: string }>({
    open: false,
  });
  const [drawConfirmModal, setDrawConfirmModal] = useState<{ open: boolean; roundId?: string; winnersCount: string }>({
    open: false,
    winnersCount: '1',
  });
  const [completingRoundId, setCompletingRoundId] = useState<string | null>(null);
  const [expandedRoundId, setExpandedRoundId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'rounds' | 'validation'>('rounds');

  const { data: rounds = [], isLoading } = useQuery<ContestRound[]>({
    queryKey: ['contest-rounds', contestId],
    queryFn: () =>
      api.get(`/api/v1/loyalty/contests/${contestId}/rounds`).then((r) => r.data),
    enabled: !!contestId,
  });

  const createRoundMutation = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/loyalty/contests/${contestId}/rounds`, {
        name: roundName,
        startAt: new Date(roundStart).toISOString(),
        endAt: new Date(roundEnd).toISOString(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contest-rounds', contestId] });
      toast.success('Round created');
      setAddRoundOpen(false);
      setRoundName('');
      setRoundStart('');
      setRoundEnd('');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  // ── Round Builder state ────────────────────────────────────────────────────
  const [builderModal, setBuilderModal] = useState(false);
  const [builderStart, setBuilderStart] = useState('');
  const [builderEnd, setBuilderEnd] = useState('');
  const [builderDurValue, setBuilderDurValue] = useState('1');
  const [builderDurUnit, setBuilderDurUnit] = useState<'minutes' | 'hours' | 'days'>('hours');
  const [builderOverwriteCount, setBuilderOverwriteCount] = useState<number | null>(null);

  const builderRoundCount = (() => {
    if (!builderStart || !builderEnd) return 0;
    const start = new Date(builderStart).getTime();
    const end = new Date(builderEnd).getTime();
    if (end <= start) return 0;
    const sliceMs =
      builderDurUnit === 'minutes' ? Number(builderDurValue) * 60_000 :
      builderDurUnit === 'hours'   ? Number(builderDurValue) * 3_600_000 :
      Number(builderDurValue) * 86_400_000;
    return sliceMs > 0 ? Math.ceil((end - start) / sliceMs) : 0;
  })();

  const generateRoundsMutation = useMutation({
    mutationFn: (overwrite: boolean) => {
      const startDate = new Date(builderStart);
      const endDate = new Date(builderEnd);
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return Promise.reject(new Error('Invalid date range'));
      }
      return api.post(`/api/v1/loyalty/contests/${contestId}/rounds/generate`, {
        startAt: startDate.toISOString(),
        endAt: endDate.toISOString(),
        duration: { value: Number(builderDurValue), unit: builderDurUnit },
        overwrite,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contest-rounds', contestId] });
      toast.success('Rounds generated');
      setBuilderModal(false);
      setBuilderOverwriteCount(null);
      setBuilderStart('');
      setBuilderEnd('');
      setBuilderDurValue('1');
      setBuilderDurUnit('hours');
    },
    onError: (err: any) => {
      if (err?.response?.status === 409) {
        const count = err?.response?.data?.existingCount;
        setBuilderOverwriteCount(typeof count === 'number' ? count : 0);
      } else {
        toast.error(extractError(err));
      }
    },
  });

  const completeRoundMutation = useMutation({
    mutationFn: (roundId: string) => {
      setCompletingRoundId(roundId);
      return api.post(`/api/v1/loyalty/contests/${contestId}/rounds/${roundId}/complete`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contest-rounds', contestId] });
      toast.info('Round completed');
    },
    onError: (err) => toast.error(extractError(err)),
    onSettled: () => setCompletingRoundId(null),
  });

  const executeDrawMutation = useMutation({
    mutationFn: ({ roundId, winnersCount }: { roundId: string; winnersCount: number }) =>
      api.post(`/api/v1/loyalty/contests/${contestId}/rounds/${roundId}/execute-draw`, {
        winnersCount,
      }),
    onSuccess: () => {
      toast.success('Draw executed');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const ROUND_STATUS_COLORS: Record<string, string> = {
    upcoming: 'bg-zinc-100 text-zinc-500',
    active: 'bg-emerald-100 text-emerald-700',
    completed: 'bg-sky-100 text-sky-700',
  };

  return (
    <div>
      {/* Panel tabs: Rounds | Validation Queue */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-1">
          {(['rounds', 'validation'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                viewMode === mode
                  ? 'bg-zinc-800 text-white'
                  : 'text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100'
              }`}
            >
              {mode === 'rounds' ? 'Rounds' : 'Validation Queue'}
            </button>
          ))}
        </div>
        {viewMode === 'rounds' && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => setBuilderModal(true)}>
              <Wand2 size={12} className="mr-1" /> Round Builder
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAddRoundOpen(!addRoundOpen)}>
              <Plus size={12} className="mr-1" /> Add round
            </Button>
          </div>
        )}
      </div>

      {viewMode === 'validation' && <ValidationQueue contestId={contestId} />}
      {viewMode === 'rounds' && (<>

      {addRoundOpen && (
        <div className="mb-4 p-3 border border-zinc-200 rounded-lg bg-white space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Input
              label="Round name"
              value={roundName}
              onChange={(e) => setRoundName(e.target.value)}
              placeholder="e.g. Week 1"
            />
            <Input
              label="Start date"
              type="datetime-local"
              value={roundStart}
              onChange={(e) => setRoundStart(e.target.value)}
            />
            <Input
              label="End date"
              type="datetime-local"
              value={roundEnd}
              onChange={(e) => setRoundEnd(e.target.value)}
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setAddRoundOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              loading={createRoundMutation.isPending}
              disabled={!roundName || !roundStart || !roundEnd}
              onClick={() => createRoundMutation.mutate()}
            >
              Create round
            </Button>
          </div>
        </div>
      )}

      {builderModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => { setBuilderModal(false); setBuilderOverwriteCount(null); }}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            {builderOverwriteCount !== null ? (
              <>
                <h3 className="text-sm font-semibold text-zinc-800">Confirm overwrite</h3>
                <p className="text-sm text-zinc-600">
                  This contest has <strong>{builderOverwriteCount}</strong> existing round{builderOverwriteCount !== 1 ? 's' : ''}.
                  They will be permanently deleted and replaced with the new rounds.
                </p>
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="ghost" onClick={() => setBuilderOverwriteCount(null)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={generateRoundsMutation.isPending}
                    onClick={() => generateRoundsMutation.mutate(true)}
                  >
                    Delete &amp; Generate
                  </Button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-sm font-semibold text-zinc-800">Round Builder</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Start"
                    type="datetime-local"
                    value={builderStart}
                    onChange={(e) => setBuilderStart(e.target.value)}
                  />
                  <Input
                    label="End"
                    type="datetime-local"
                    value={builderEnd}
                    onChange={(e) => setBuilderEnd(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Input
                    label="Duration"
                    type="number"
                    min="1"
                    value={builderDurValue}
                    onChange={(e) => setBuilderDurValue(e.target.value)}
                    className="w-24"
                  />
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-zinc-600 mb-1">Unit</label>
                    <select
                      value={builderDurUnit}
                      onChange={(e) => setBuilderDurUnit(e.target.value as 'minutes' | 'hours' | 'days')}
                      className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm"
                    >
                      <option value="minutes">Minutes</option>
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                    </select>
                  </div>
                </div>
                {builderRoundCount > 0 && (
                  <p className="text-xs text-zinc-500">
                    Will generate <strong>{builderRoundCount}</strong> round{builderRoundCount !== 1 ? 's' : ''}
                  </p>
                )}
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="ghost" onClick={() => { setBuilderModal(false); setBuilderOverwriteCount(null); }}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    loading={generateRoundsMutation.isPending}
                    disabled={builderRoundCount === 0 || !Number(builderDurValue)}
                    onClick={() => generateRoundsMutation.mutate(false)}
                  >
                    Generate
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-xs text-zinc-400 py-2">Loading rounds…</div>
      ) : rounds.length === 0 ? (
        <div className="text-xs text-zinc-400 py-2">No rounds yet. Add the first round above.</div>
      ) : (
        <div className="space-y-2">
          {rounds.map((r) => (
            <div
              key={r.id}
              className="bg-white border border-zinc-200 rounded-lg overflow-hidden"
            >
              {/* Round header row */}
              <div className="flex items-center justify-between p-3">
                <div className="flex items-center gap-3">
                  <span
                    className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full capitalize ${ROUND_STATUS_COLORS[r.status] ?? 'bg-zinc-100 text-zinc-500'}`}
                  >
                    {r.status}
                  </span>
                  <button
                    onClick={() => setExpandedRoundId(expandedRoundId === r.id ? null : r.id)}
                    className="text-sm font-medium text-zinc-800 hover:text-zinc-500 transition-colors flex items-center gap-1"
                  >
                    {r.name}
                    <ChevronDown
                      size={12}
                      className={`transition-transform ${expandedRoundId === r.id ? 'rotate-180' : ''}`}
                    />
                  </button>
                  <span className="text-xs text-zinc-400">
                    {fmt(r.startAt)} — {fmt(r.endAt)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setBulkModal({ open: true, roundId: r.id })}
                  >
                    <Package size={12} className="mr-1" /> Add Prizes
                  </Button>
                {contestType === 'draw' && r.status !== 'completed' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDrawConfirmModal({ open: true, roundId: r.id, winnersCount: '1' })}
                    loading={executeDrawMutation.isPending && drawConfirmModal.roundId === r.id}
                  >
                    <Zap size={12} className="mr-1" /> Draw
                  </Button>
                )}
                {r.status !== 'completed' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => completeRoundMutation.mutate(r.id)}
                    loading={completingRoundId === r.id}
                  >
                    <Check size={12} className="mr-1" /> Complete
                  </Button>
                )}
              </div>
              </div>
              {/* Expandable prize slots */}
              {expandedRoundId === r.id && (
                <div className="border-t border-zinc-100 bg-zinc-50 px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 mb-1">
                    Prize Slots
                  </p>
                  <PrizeSlotList contestId={contestId} roundId={r.id} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {bulkModal.open && bulkModal.roundId && (
        <BulkCreatePrizesModal
          open={bulkModal.open}
          contestId={contestId}
          roundId={bulkModal.roundId}
          onClose={() => setBulkModal({ open: false })}
        />
      )}

      {/* Draw confirmation modal */}
      {drawConfirmModal.open && drawConfirmModal.roundId && (
        <Modal
          open={drawConfirmModal.open}
          onClose={() => setDrawConfirmModal({ open: false, winnersCount: '1' })}
          title="Execute Draw"
        >
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">
              This will randomly pick winners from all draw entries for this round.
              This action cannot be undone.
            </p>
            <Input
              label="Number of winners"
              type="number"
              min="1"
              value={drawConfirmModal.winnersCount}
              onChange={(e) => setDrawConfirmModal((m) => ({ ...m, winnersCount: e.target.value }))}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => setDrawConfirmModal({ open: false, winnersCount: '1' })}
              >
                Cancel
              </Button>
              <Button
                loading={executeDrawMutation.isPending}
                onClick={() => {
                  const count = Math.max(1, parseInt(drawConfirmModal.winnersCount, 10) || 1);
                  executeDrawMutation.mutate(
                    { roundId: drawConfirmModal.roundId!, winnersCount: count },
                    { onSettled: () => setDrawConfirmModal({ open: false, winnersCount: '1' }) },
                  );
                }}
              >
                <Zap size={13} className="mr-1" /> Execute Draw
              </Button>
            </div>
          </div>
        </Modal>
      )}
      </>)}
    </div>
  );
}

// ─── BulkCreatePrizesModal ────────────────────────────────────────────────────

function BulkCreatePrizesModal({
  open,
  contestId,
  roundId,
  onClose,
}: {
  open: boolean;
  contestId: string;
  roundId: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const [count, setCount] = useState('1');
  const [prizeName, setPrizeName] = useState('');
  const [prizeType, setPrizeType] = useState<
    'points' | 'coupon' | 'physical' | 'voucher' | 'custom'
  >('points');
  const [prizeValueJson, setPrizeValueJson] = useState('{}');

  const mutation = useMutation({
    mutationFn: () => {
      let prizeValue: Record<string, unknown> = {};
      try {
        prizeValue = JSON.parse(prizeValueJson) as Record<string, unknown>;
      } catch {
        // fallback to empty object
      }
      return api.post(
        `/api/v1/loyalty/contests/${contestId}/rounds/${roundId}/prizes/bulk-create`,
        {
          count: Number(count),
          template: { name: prizeName, prizeType, prizeValue },
        },
      );
    },
    onSuccess: () => {
      toast.success(`${count} prize(s) created`);
      onClose();
    },
    onError: (err) => toast.error(extractError(err)),
  });

  return (
    <Modal open={open} onClose={onClose} title="Bulk create prizes">
      <div className="space-y-4">
        <Input
          label="Number of prizes"
          type="number"
          min="1"
          max="1000"
          value={count}
          onChange={(e) => setCount(e.target.value)}
        />
        <Input
          label="Prize name"
          value={prizeName}
          onChange={(e) => setPrizeName(e.target.value)}
          placeholder="e.g. Gold medal"
        />
        <Select
          label="Prize type"
          value={prizeType}
          onChange={(e) =>
            setPrizeType(
              e.target.value as 'points' | 'coupon' | 'physical' | 'voucher' | 'custom',
            )
          }
        >
          <option value="points">Points</option>
          <option value="coupon">Coupon</option>
          <option value="physical">Physical</option>
          <option value="voucher">Voucher</option>
          <option value="custom">Custom</option>
        </Select>
        <Textarea
          label="Prize value (JSON)"
          value={prizeValueJson}
          onChange={(e) => setPrizeValueJson(e.target.value)}
          rows={3}
          placeholder='{"amount": 500}'
        />
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={mutation.isPending}
            disabled={!prizeName || !count}
            onClick={() => mutation.mutate()}
          >
            Create {count} prize(s)
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── ContestModal ─────────────────────────────────────────────────────────────

function ContestModal({
  open,
  contest,
  programId,
  onClose,
  onSaved,
}: {
  open: boolean;
  contest?: Contest;
  programId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [title, setTitle] = useState(contest?.title ?? '');
  const [description, setDescription] = useState(contest?.description ?? '');
  const [contestType, setContestType] = useState<'instant_win' | 'draw' | 'leaderboard'>(
    contest?.contestType ?? 'draw',
  );
  const [startDate, setStartDate] = useState(contest?.startDate?.slice(0, 10) ?? '');
  const [endDate, setEndDate] = useState(contest?.endDate?.slice(0, 10) ?? '');
  const [winValidation, setWinValidation] = useState<'auto' | 'manual'>(
    contest?.winValidation ?? 'manual',
  );
  const [configJson, setConfigJson] = useState(
    contest?.config ? JSON.stringify(contest.config, null, 2) : '{}',
  );
  // Instant Win typed config fields
  const existingConfig = contest?.config as Record<string, unknown> | undefined;
  const [iwWinRate, setIwWinRate] = useState(Number(existingConfig?.winRate ?? 15));
  const [iwWinRateRecovered, setIwWinRateRecovered] = useState(Number(existingConfig?.winRateRecovered ?? 35));
  const [iwMaxWinsPerRound, setIwMaxWinsPerRound] = useState<number | null>(
    existingConfig?.maxWinsPerRound != null ? Number(existingConfig.maxWinsPerRound) : null,
  );
  const [iwMaxWinsPerContest, setIwMaxWinsPerContest] = useState<number | null>(
    existingConfig?.maxWinsPerContest != null ? Number(existingConfig.maxWinsPerContest) : null,
  );
  const [iwConsolationPoints, setIwConsolationPoints] = useState(Number(existingConfig?.consolationPoints ?? 10));
  const [iwGameUI, setIwGameUI] = useState(String(existingConfig?.gameUI ?? 'scratch_card'));
  const [iwMaxRetries, setIwMaxRetries] = useState(Number(existingConfig?.maxRetriesOnDeadlock ?? 5));
  const existingEntryConfig = contest?.entryConfig as Record<string, unknown> | undefined;
  const [entryConfigType, setEntryConfigType] = useState<'points' | 'free' | 'unlimited' | 'mission'>(
    existingEntryConfig?.type === 'points' ? 'points'
    : existingEntryConfig?.type === 'unlimited' ? 'unlimited'
    : existingEntryConfig?.type === 'mission' ? 'mission'
    : 'free',
  );
  const [entryPointsThreshold, setEntryPointsThreshold] = useState(
    String(existingEntryConfig?.threshold ?? 100),
  );
  const [entryMaxPlaysPerRound, setEntryMaxPlaysPerRound] = useState(
    String(existingEntryConfig?.maxPlaysPerRound ?? 1),
  );
  const existingReqs = existingEntryConfig?.requirements as Record<string, unknown> | undefined;
  const [entryMissionId, setEntryMissionId] = useState(String(existingEntryConfig?.missionId ?? ''));
  const [entryMinTier, setEntryMinTier] = useState(String(existingReqs?.minTier ?? ''));
  const [entryMinPoints, setEntryMinPoints] = useState(String(existingReqs?.minPoints ?? ''));
  const [entryBadgeCode, setEntryBadgeCode] = useState(String(existingReqs?.badgeCode ?? ''));

  const mutation = useMutation({
    mutationFn: () => {
      let config: Record<string, unknown> = {};
      if (contestType === 'instant_win') {
        config = {
          winRate: iwWinRate,
          winRateRecovered: iwWinRateRecovered,
          maxWinsPerRound: iwMaxWinsPerRound,
          maxWinsPerContest: iwMaxWinsPerContest,
          consolationPoints: iwConsolationPoints,
          gameUI: iwGameUI,
          maxRetriesOnDeadlock: iwMaxRetries,
        };
      } else {
        try {
          config = JSON.parse(configJson) as Record<string, unknown>;
        } catch {
          // fallback to empty
        }
      }
      const freeEntryRequirements: Record<string, unknown> = {};
      if (entryMinTier) freeEntryRequirements.minTier = entryMinTier;
      if (entryMinPoints) freeEntryRequirements.minPoints = Number(entryMinPoints);
      if (entryBadgeCode) freeEntryRequirements.badgeCode = entryBadgeCode;
      const entryConfig: Record<string, unknown> =
        entryConfigType === 'points'
          ? { type: 'points', threshold: Number(entryPointsThreshold), periodType: 'round' }
          : entryConfigType === 'unlimited'
          ? { type: 'unlimited' }
          : entryConfigType === 'mission'
          ? { type: 'mission', missionId: entryMissionId }
          : {
              type: 'free_entry',
              maxPlaysPerRound: Number(entryMaxPlaysPerRound),
              ...(Object.keys(freeEntryRequirements).length > 0 ? { requirements: freeEntryRequirements } : {}),
            };

      const body = {
        title,
        description: description || null,
        contestType,
        startDate,
        endDate,
        winValidation,
        config,
        entryConfig,
      };
      return contest
        ? api.patch(`/api/v1/loyalty/contests/${contest.id}`, body)
        : api.post('/api/v1/loyalty/contests', { ...body, programId });
    },
    onSuccess: () => {
      onSaved();
      toast.success(contest ? 'Contest updated' : 'Contest created');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={contest ? 'Edit contest' : 'New contest'}
      description="Configure a contest for this loyalty program."
    >
      <div className="space-y-4">
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Champions Draw"
        />
        <Textarea
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />
        <Select
          label="Contest type"
          value={contestType}
          onChange={(e) =>
            setContestType(e.target.value as 'instant_win' | 'draw' | 'leaderboard')
          }
        >
          <option value="instant_win">Instant Win</option>
          <option value="draw">Draw</option>
          <option value="leaderboard">Leaderboard</option>
        </Select>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Start date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Input
            label="End date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <Select
          label="Win validation"
          value={winValidation}
          onChange={(e) => setWinValidation(e.target.value as 'auto' | 'manual')}
        >
          <option value="manual">Manual</option>
          <option value="auto">Auto</option>
        </Select>
        <Select
          label="Access type"
          value={entryConfigType}
          onChange={(e) => setEntryConfigType(e.target.value as 'points' | 'free' | 'unlimited' | 'mission')}
        >
          <option value="free">Free (N plays per round)</option>
          <option value="unlimited">Unlimited (always playable)</option>
          <option value="points">Points-based</option>
          <option value="mission">Mission-based</option>
        </Select>
        {entryConfigType === 'free' && (
          <>
            <Input
              label="Plays per round"
              type="number"
              min="1"
              value={entryMaxPlaysPerRound}
              onChange={(e) => setEntryMaxPlaysPerRound(e.target.value)}
            />
            <details className="text-xs text-zinc-400">
              <summary className="cursor-pointer hover:text-zinc-600 select-none">Requirements (opzionali)</summary>
              <div className="mt-2 space-y-2">
                <Input
                  label="Min tier"
                  value={entryMinTier}
                  onChange={(e) => setEntryMinTier(e.target.value)}
                  placeholder="es. gold"
                />
                <Input
                  label="Min points"
                  type="number"
                  min="0"
                  value={entryMinPoints}
                  onChange={(e) => setEntryMinPoints(e.target.value)}
                />
                <Input
                  label="Badge code"
                  value={entryBadgeCode}
                  onChange={(e) => setEntryBadgeCode(e.target.value)}
                />
              </div>
            </details>
          </>
        )}
        {entryConfigType === 'points' && (
          <Input
            label="Points threshold (points per play)"
            type="number"
            min="1"
            value={entryPointsThreshold}
            onChange={(e) => setEntryPointsThreshold(e.target.value)}
          />
        )}
        {entryConfigType === 'mission' && (
          <Input
            label="Mission ID"
            value={entryMissionId}
            onChange={(e) => setEntryMissionId(e.target.value)}
            placeholder="ID della missione richiesta"
          />
        )}
        {contestType === 'instant_win' ? (
          <div className="space-y-3 rounded-lg border border-zinc-200 bg-zinc-50 p-3">
            <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Instant Win Settings</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-zinc-700 mb-1">Win Rate (%)</label>
                <input type="range" min={0} max={100} value={iwWinRate} onChange={e => setIwWinRate(Number(e.target.value))}
                  className="w-full accent-zinc-900" />
                <div className="text-right text-xs text-zinc-500">{iwWinRate}%</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-700 mb-1">Recovered Win Rate (%)</label>
                <input type="range" min={0} max={100} value={iwWinRateRecovered} onChange={e => setIwWinRateRecovered(Number(e.target.value))}
                  className="w-full accent-zinc-900" />
                <div className="text-right text-xs text-zinc-500">{iwWinRateRecovered}%</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="flex items-center gap-2 text-xs font-medium text-zinc-700 mb-1">
                  Max wins / round
                  <input type="checkbox" checked={iwMaxWinsPerRound === null}
                    onChange={e => setIwMaxWinsPerRound(e.target.checked ? null : 5)}
                    className="accent-zinc-900" />
                  <span className="text-zinc-400 font-normal">Unlimited</span>
                </label>
                {iwMaxWinsPerRound !== null && (
                  <Input type="number" min="1" value={String(iwMaxWinsPerRound)}
                    onChange={e => setIwMaxWinsPerRound(Number(e.target.value))} />
                )}
              </div>
              <div>
                <label className="flex items-center gap-2 text-xs font-medium text-zinc-700 mb-1">
                  Max wins / contest
                  <input type="checkbox" checked={iwMaxWinsPerContest === null}
                    onChange={e => setIwMaxWinsPerContest(e.target.checked ? null : 10)}
                    className="accent-zinc-900" />
                  <span className="text-zinc-400 font-normal">Unlimited</span>
                </label>
                {iwMaxWinsPerContest !== null && (
                  <Input type="number" min="1" value={String(iwMaxWinsPerContest)}
                    onChange={e => setIwMaxWinsPerContest(Number(e.target.value))} />
                )}
              </div>
            </div>
            <Input label="Consolation points (on lose)" type="number" min="0"
              value={String(iwConsolationPoints)}
              onChange={e => setIwConsolationPoints(Number(e.target.value))} />
            <Select label="Game UI" value={iwGameUI} onChange={e => setIwGameUI(e.target.value)}>
              <option value="scratch_card">Scratch Card</option>
              <option value="spin_wheel">Spin Wheel</option>
              <option value="slot_machine">Slot Machine</option>
              <option value="gift_box">Gift Box</option>
              <option value="none">None (instant reveal)</option>
            </Select>
            <details className="text-xs text-zinc-400">
              <summary className="cursor-pointer hover:text-zinc-600">Advanced</summary>
              <div className="mt-2">
                <Input label="Max retries on deadlock" type="number" min="1" max="20"
                  value={String(iwMaxRetries)}
                  onChange={e => setIwMaxRetries(Number(e.target.value))} />
              </div>
            </details>
          </div>
        ) : (
          <Textarea
            label="Config (JSON, advanced)"
            value={configJson}
            onChange={(e) => setConfigJson(e.target.value)}
            rows={3}
          />
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={mutation.isPending}
            disabled={!title || !startDate || !endDate}
            onClick={() => mutation.mutate()}
          >
            {contest ? 'Save' : 'Create contest'}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── LeaderboardTab ───────────────────────────────────────────────────────────

function LeaderboardTab({ programId }: { programId: string }) {
  const [period, setPeriod] = useState<'weekly' | 'monthly'>('weekly');

  const { data: entries = [], isLoading } = useQuery<LeaderboardEntry[]>({
    queryKey: ['loyalty-leaderboard', programId, period],
    queryFn: () =>
      api
        .get(`/api/v1/loyalty/leaderboard?programId=${programId}&periodType=${period}&limit=50`)
        .then((r) => r.data),
    enabled: !!programId,
  });

  const DELTA_ICON = (delta: number | null) => {
    if (delta === null || delta === 0) return <span className="text-zinc-300 text-[10px]">—</span>;
    if (delta > 0)
      return <span className="text-emerald-600 text-[10px] font-semibold">▲{delta}</span>;
    return <span className="text-rose-600 text-[10px] font-semibold">▼{Math.abs(delta)}</span>;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-zinc-500">Top 50 members ranked by points earned in period.</p>
        <div className="flex items-center gap-1 p-1 bg-zinc-100 rounded-lg">
          {(['weekly', 'monthly'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors capitalize ${
                period === p
                  ? 'bg-white text-zinc-900 shadow-sm'
                  : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100">
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider w-16">
                Rank
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Score
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Change
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-50">
            {isLoading ? (
              <SkeletonRows count={10} cols={4} />
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-12 text-center text-sm text-zinc-400">
                  No leaderboard data for this period.
                </td>
              </tr>
            ) : (
              entries.map((e, idx) => (
                <tr
                  key={e.customerId}
                  className={`hover:bg-zinc-50/40 ${idx < 3 ? 'bg-amber-50/30' : ''}`}
                >
                  <td className="px-4 py-2.5">
                    <span
                      className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold ${
                        e.rank === 1
                          ? 'bg-amber-400 text-white'
                          : e.rank === 2
                            ? 'bg-zinc-300 text-zinc-700'
                            : e.rank === 3
                              ? 'bg-amber-700/60 text-white'
                              : 'bg-zinc-100 text-zinc-600'
                      }`}
                    >
                      {e.rank}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <span className="flex items-center justify-center w-7 h-7 rounded-full bg-zinc-100 text-zinc-600 text-xs font-medium flex-shrink-0">
                        {e.avatarUrl ? (
                          <img
                            src={e.avatarUrl}
                            alt=""
                            className="w-7 h-7 rounded-full object-cover"
                          />
                        ) : (
                          initials(e.firstName, e.lastName, `rank-${e.rank}`)
                        )}
                      </span>
                      <span className="text-sm font-medium text-zinc-900">
                        {fullName(e.firstName, e.lastName, `#${e.rank}`)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 font-bold text-zinc-800 tabular-nums">
                    {e.score.toLocaleString('en')}
                  </td>
                  <td className="px-4 py-2.5">{DELTA_ICON(e.deltaRank)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── EnrollCustomerModal ──────────────────────────────────────────────────────

function EnrollCustomerModal({
  open,
  programId,
  onClose,
  onSaved,
}: {
  open: boolean;
  programId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<{
    id: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
  } | null>(null);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setSelectedCustomer(null);
    }
  }, [open]);

  const { data: customers = [], isFetching } = useQuery<
    { id: string; email: string | null; firstName: string | null; lastName: string | null }[]
  >({
    queryKey: ['customers-search', search],
    queryFn: () =>
      api
        .get(`/api/v1/customers?search=${encodeURIComponent(search)}&limit=10`)
        .then((r) => r.data?.items ?? r.data ?? []),
    enabled: search.length >= 2,
  });

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/loyalty/programs/${programId}/members`, {
        customerId: selectedCustomer!.id,
      }),
    onSuccess: () => {
      onSaved();
      toast.success('Customer enrolled in program');
    },
    onError: (err) => toast.error(extractError(err, 'Enrollment error')),
  });

  const showDropdown = search.length >= 2 && !selectedCustomer;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Enroll customer"
      description="Search and select a customer to enroll in the program."
    >
      <div className="space-y-4">
        <Input
          label="Search customer (name or email)"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setSelectedCustomer(null);
          }}
          placeholder="type at least 2 characters..."
        />
        {showDropdown &&
          (isFetching ? (
            <div className="flex items-center gap-2 px-3 py-2.5 text-xs text-zinc-400">
              <span className="w-3 h-3 rounded-full border-2 border-zinc-300 border-t-zinc-600 animate-spin flex-shrink-0" />
              Searching...
            </div>
          ) : customers.length === 0 ? (
            <p className="px-3 py-2.5 text-xs text-zinc-400">No customers found for "{search}"</p>
          ) : (
            <div className="border border-zinc-200 rounded-lg overflow-hidden divide-y divide-zinc-100">
              {customers.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelectedCustomer(c)}
                  className="w-full px-3 py-2.5 text-left hover:bg-zinc-50 transition-colors"
                >
                  <p className="text-sm font-medium text-zinc-900">
                    {fullName(c.firstName, c.lastName, c.email ?? '')}
                  </p>
                  <p className="text-xs text-zinc-400">{c.email ?? '—'}</p>
                </button>
              ))}
            </div>
          ))}
        {selectedCustomer && (
          <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div>
              <p className="text-sm font-medium text-emerald-900">
                {fullName(
                  selectedCustomer.firstName,
                  selectedCustomer.lastName,
                  selectedCustomer.email ?? '',
                )}
              </p>
              <p className="text-xs text-emerald-600">{selectedCustomer.email ?? '—'}</p>
            </div>
            <button
              onClick={() => setSelectedCustomer(null)}
              className="text-xs text-emerald-600 hover:text-emerald-800"
            >
              Change
            </button>
          </div>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={mutation.isPending}
            disabled={!selectedCustomer}
            onClick={() => mutation.mutate()}
          >
            Enroll
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── MissionsTab ──────────────────────────────────────────────────────────────

const MISSION_ACTION_LABELS: Record<string, string> = {
  daily_login: 'Daily Login',
  article_read: 'Article Read',
  quiz_complete: 'Quiz Complete',
  social_share: 'Social Share',
  profile_update: 'Profile Update',
  referral: 'Referral',
  custom: 'Custom',
};
const MISSION_RECURRENCE_LABELS: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  one_time: 'One Time',
};
const DAY_OF_WEEK_LABELS: Record<number, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

function MissionsTab({ programId }: { programId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [modal, setModal] = useState<{ open: boolean; mission?: Mission }>({ open: false });
  const [deleteTarget, setDeleteTarget] = useState<Mission | null>(null);

  const { data: missions = [], isLoading } = useQuery<Mission[]>({
    queryKey: ['loyalty-missions', programId],
    queryFn: () => api.get(`/api/v1/loyalty/programs/${programId}/missions`).then((r) => r.data),
    enabled: !!programId,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ missionId, isActive }: { missionId: string; isActive: boolean }) =>
      api.patch(`/api/v1/loyalty/programs/${programId}/missions/${missionId}`, { isActive }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-missions', programId] });
      toast.success('Mission updated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (missionId: string) =>
      api.delete(`/api/v1/loyalty/programs/${programId}/missions/${missionId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-missions', programId] });
      setDeleteTarget(null);
      toast.info('Mission deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-zinc-500">
          Daily missions encourage customers to perform specific actions to earn bonus points.
        </p>
        <Button size="sm" onClick={() => setModal({ open: true })}>
          <Plus size={13} className="mr-1.5" /> Add mission
        </Button>
      </div>
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100">
              <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Title
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Action Type
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Points
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Recurrence
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Active
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-50">
            {isLoading ? (
              <SkeletonRows count={4} cols={6} />
            ) : missions.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-zinc-400">
                  No missions defined yet
                </td>
              </tr>
            ) : (
              missions.map((m) => (
                <tr key={m.id} className="hover:bg-zinc-50/60 transition-colors">
                  <td className="px-4 py-2.5 font-medium text-zinc-900">{m.title}</td>
                  <td className="px-4 py-2.5 text-zinc-600">
                    {MISSION_ACTION_LABELS[m.actionType] ?? m.actionType}
                  </td>
                  <td className="px-4 py-2.5 text-zinc-700 tabular-nums">{m.pointsReward}</td>
                  <td className="px-4 py-2.5 text-zinc-600">
                    {MISSION_RECURRENCE_LABELS[m.recurrence] ?? m.recurrence}
                    {m.recurrence === 'weekly' && m.dayOfWeek != null && (
                      <span className="text-zinc-400 ml-1">
                        ({DAY_OF_WEEK_LABELS[m.dayOfWeek] ?? m.dayOfWeek})
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge variant={m.isActive ? 'success' : 'default'} className="text-[10px]">
                      {m.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-0.5">
                      <button
                        onClick={() => setModal({ open: true, mission: m })}
                        className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                      >
                        <Pencil size={11} />
                      </button>
                      <button
                        onClick={() =>
                          toggleMutation.mutate({ missionId: m.id, isActive: !m.isActive })
                        }
                        className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                      >
                        {m.isActive ? (
                          <ToggleRight size={13} className="text-emerald-500" />
                        ) : (
                          <ToggleLeft size={13} />
                        )}
                      </button>
                      <button
                        onClick={() => setDeleteTarget(m)}
                        className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <MissionModal
        key={modal.mission?.id ?? 'new-mission'}
        open={modal.open}
        mission={modal.mission}
        programId={programId}
        onClose={() => setModal({ open: false })}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['loyalty-missions', programId] });
          setModal({ open: false });
        }}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete mission?"
        message={`Mission "${deleteTarget?.title}" will be permanently deleted. Existing customer progress will be lost.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function MissionModal({
  open,
  mission,
  programId,
  onClose,
  onSaved,
}: {
  open: boolean;
  mission?: Mission;
  programId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [title, setTitle] = useState(mission?.title ?? '');
  const [description, setDescription] = useState(mission?.description ?? '');
  const [actionType, setActionType] = useState(mission?.actionType ?? 'daily_login');
  const [requiredCount, setRequiredCount] = useState(String(mission?.requiredCount ?? 1));
  const [pointsReward, setPointsReward] = useState(String(mission?.pointsReward ?? 0));
  const [recurrence, setRecurrence] = useState(mission?.recurrence ?? 'daily');
  const [dayOfWeek, setDayOfWeek] = useState(
    mission?.dayOfWeek != null ? String(mission.dayOfWeek) : '',
  );
  const [sortOrder, setSortOrder] = useState(String(mission?.sortOrder ?? 0));
  const [isActive, setIsActive] = useState(mission?.isActive ?? true);

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        title,
        description: description || null,
        actionType,
        requiredCount: Number(requiredCount),
        pointsReward: Number(pointsReward),
        recurrence,
        dayOfWeek: recurrence === 'weekly' && dayOfWeek !== '' ? Number(dayOfWeek) : null,
        sortOrder: Number(sortOrder),
        isActive,
      };
      return mission
        ? api.patch(`/api/v1/loyalty/programs/${programId}/missions/${mission.id}`, body)
        : api.post(`/api/v1/loyalty/programs/${programId}/missions`, body);
    },
    onSuccess: () => {
      onSaved();
      toast.success(mission ? 'Mission updated' : 'Mission created');
    },
    onError: (err) => toast.error(extractError(err, 'Error saving mission')),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mission ? 'Edit mission' : 'New mission'}
      description="Define an action-based mission that customers can complete to earn points."
    >
      <div className="space-y-4">
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Read 3 articles today"
        />
        <Textarea
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Action type"
            value={actionType}
            onChange={(e) => setActionType(e.target.value)}
          >
            {Object.entries(MISSION_ACTION_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
          <Input
            label="Required count"
            type="number"
            min="1"
            value={requiredCount}
            onChange={(e) => setRequiredCount(e.target.value)}
          />
        </div>
        <Input
          label="Points reward"
          type="number"
          min="0"
          value={pointsReward}
          onChange={(e) => setPointsReward(e.target.value)}
        />
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Recurrence"
            value={recurrence}
            onChange={(e) => setRecurrence(e.target.value)}
          >
            {Object.entries(MISSION_RECURRENCE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
          {recurrence === 'weekly' && (
            <Select
              label="Day of week"
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(e.target.value)}
            >
              <option value="">Any day</option>
              {Object.entries(DAY_OF_WEEK_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Sort order"
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Active</label>
            <button
              type="button"
              onClick={() => setIsActive(!isActive)}
              className="flex items-center gap-2 text-sm text-zinc-600"
            >
              {isActive ? (
                <ToggleRight size={20} className="text-emerald-500" />
              ) : (
                <ToggleLeft size={20} className="text-zinc-400" />
              )}
              {isActive ? 'Active' : 'Inactive'}
            </button>
          </div>
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" loading={mutation.isPending} disabled={!title} onClick={() => mutation.mutate()}>
          {mission ? 'Save changes' : 'Create mission'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── FlashRewardsTab ──────────────────────────────────────────────────────────

const FLASH_REWARD_TYPE_LABELS: Record<string, string> = {
  bonus_points: 'Bonus Points',
  points_multiplier: 'Points Multiplier',
  free_reward: 'Free Reward',
  discount: 'Discount',
};
const FLASH_STATUS_LABELS: Record<string, string> = {
  scheduled: 'Scheduled',
  active: 'Active',
  expired: 'Expired',
  exhausted: 'Exhausted',
};
const FLASH_STATUS_COLORS: Record<string, string> = {
  scheduled: 'bg-zinc-100 text-zinc-600',
  active: 'bg-emerald-100 text-emerald-700',
  expired: 'bg-red-100 text-red-600',
  exhausted: 'bg-amber-100 text-amber-700',
};

function FlashRewardsTab({ programId }: { programId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [modal, setModal] = useState<{ open: boolean; reward?: FlashRewardItem }>({ open: false });
  const [deleteTarget, setDeleteTarget] = useState<FlashRewardItem | null>(null);

  const { data: flashRewards = [], isLoading } = useQuery<FlashRewardItem[]>({
    queryKey: ['loyalty-flash-rewards', programId],
    queryFn: () =>
      api.get(`/api/v1/loyalty/programs/${programId}/flash-rewards`).then((r) => r.data),
    enabled: !!programId,
  });

  const deleteMutation = useMutation({
    mutationFn: (rewardId: string) =>
      api.delete(`/api/v1/loyalty/programs/${programId}/flash-rewards/${rewardId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-flash-rewards', programId] });
      setDeleteTarget(null);
      toast.info('Flash reward deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-zinc-500">
          Time-limited flash rewards create urgency and drive engagement with exclusive offers.
        </p>
        <Button size="sm" onClick={() => setModal({ open: true })}>
          <Plus size={13} className="mr-1.5" /> Add flash reward
        </Button>
      </div>
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100">
              <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Title
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Reward Type
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Starts At
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Ends At
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Claims
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-50">
            {isLoading ? (
              <SkeletonRows count={4} cols={7} />
            ) : flashRewards.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-zinc-400">
                  No flash rewards yet
                </td>
              </tr>
            ) : (
              flashRewards.map((fr) => (
                <tr key={fr.id} className="hover:bg-zinc-50/60 transition-colors">
                  <td className="px-4 py-2.5 font-medium text-zinc-900">{fr.title}</td>
                  <td className="px-4 py-2.5 text-zinc-600">
                    {FLASH_REWARD_TYPE_LABELS[fr.rewardType] ?? fr.rewardType}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        FLASH_STATUS_COLORS[fr.status] ?? 'bg-zinc-100 text-zinc-600'
                      }`}
                    >
                      {FLASH_STATUS_LABELS[fr.status] ?? fr.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-600 tabular-nums">{fmtFull(fr.startsAt)}</td>
                  <td className="px-4 py-2.5 text-zinc-600 tabular-nums">{fmtFull(fr.endsAt)}</td>
                  <td className="px-4 py-2.5 text-zinc-700 tabular-nums">
                    {fr.claimedCount}
                    {fr.maxClaims != null && <span className="text-zinc-400">/{fr.maxClaims}</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-0.5">
                      <button
                        onClick={() => setModal({ open: true, reward: fr })}
                        className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                      >
                        <Pencil size={11} />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(fr)}
                        className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <FlashRewardModal
        key={modal.reward?.id ?? 'new-flash-reward'}
        open={modal.open}
        reward={modal.reward}
        programId={programId}
        onClose={() => setModal({ open: false })}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['loyalty-flash-rewards', programId] });
          setModal({ open: false });
        }}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete flash reward?"
        message={`Flash reward "${deleteTarget?.title}" will be permanently deleted.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function toDatetimeLocal(d: string | undefined): string {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

function FlashRewardModal({
  open,
  reward,
  programId,
  onClose,
  onSaved,
}: {
  open: boolean;
  reward?: FlashRewardItem;
  programId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [title, setTitle] = useState(reward?.title ?? '');
  const [description, setDescription] = useState(reward?.description ?? '');
  const [imageUrl, setImageUrl] = useState(reward?.imageUrl ?? '');
  const [rewardType, setRewardType] = useState(reward?.rewardType ?? 'bonus_points');
  const [rewardValueJson, setRewardValueJson] = useState(
    reward?.rewardValue ? JSON.stringify(reward.rewardValue) : '{}',
  );
  const [startsAt, setStartsAt] = useState(toDatetimeLocal(reward?.startsAt));
  const [endsAt, setEndsAt] = useState(toDatetimeLocal(reward?.endsAt));
  const [maxClaims, setMaxClaims] = useState(
    reward?.maxClaims != null ? String(reward.maxClaims) : '',
  );
  const [minTierCode, setMinTierCode] = useState(reward?.minTierCode ?? '');
  const [minPoints, setMinPoints] = useState(
    reward?.minPoints != null ? String(reward.minPoints) : '',
  );
  const [status, setStatus] = useState(reward?.status ?? 'scheduled');

  const mutation = useMutation({
    mutationFn: () => {
      let parsedValue: Record<string, unknown> = {};
      try {
        parsedValue = JSON.parse(rewardValueJson);
      } catch {
        /* keep empty */
      }
      const body = {
        title,
        description: description || null,
        imageUrl: imageUrl || null,
        rewardType,
        rewardValue: parsedValue,
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        maxClaims: maxClaims ? Number(maxClaims) : null,
        minTierCode: minTierCode || null,
        minPoints: minPoints ? Number(minPoints) : null,
        status,
      };
      return reward
        ? api.patch(`/api/v1/loyalty/programs/${programId}/flash-rewards/${reward.id}`, body)
        : api.post(`/api/v1/loyalty/programs/${programId}/flash-rewards`, body);
    },
    onSuccess: () => {
      onSaved();
      toast.success(reward ? 'Flash reward updated' : 'Flash reward created');
    },
    onError: (err) => toast.error(extractError(err, 'Error saving flash reward')),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={reward ? 'Edit flash reward' : 'New flash reward'}
      description="Create a time-limited reward that customers can claim."
    >
      <div className="space-y-4">
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Weekend Double Points"
        />
        <Textarea
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />
        <Input
          label="Image URL (optional)"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
          placeholder="https://..."
        />
        <Select
          label="Reward type"
          value={rewardType}
          onChange={(e) => setRewardType(e.target.value)}
        >
          {Object.entries(FLASH_REWARD_TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
        <Input
          label="Reward value (JSON)"
          value={rewardValueJson}
          onChange={(e) => setRewardValueJson(e.target.value)}
          placeholder='e.g. {"points": 100} or {"multiplier": 2}'
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Starts at"
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
          />
          <Input
            label="Ends at"
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Input
            label="Max claims (blank = unlimited)"
            type="number"
            min="1"
            value={maxClaims}
            onChange={(e) => setMaxClaims(e.target.value)}
          />
          <Input
            label="Min tier code (optional)"
            value={minTierCode}
            onChange={(e) => setMinTierCode(e.target.value)}
            placeholder="e.g. gold"
          />
          <Input
            label="Min points (optional)"
            type="number"
            min="0"
            value={minPoints}
            onChange={(e) => setMinPoints(e.target.value)}
          />
        </div>
        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
          {Object.entries(FLASH_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
      </div>
      <ModalFooter>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          loading={mutation.isPending}
          disabled={!title || !startsAt || !endsAt}
          onClick={() => mutation.mutate()}
        >
          {reward ? 'Save changes' : 'Create flash reward'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
